import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FlowNodeData } from '@repletoryreach/sdk-ui/dnd';
import { flows, sms, templates } from '@repletoryreach/sdk-api';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Stack, Group, Button, Text, Skeleton, ActionIcon } from '@repletoryreach/sdk-ui';
import { TextInput, Textarea, NumberInput, Select, Switch, Checkbox, PasswordInput, PhoneInput, TokenInput, TokenTextarea, Radio, RadioGroup, type TokenSuggestion } from '@repletoryreach/sdk-ui/inputs';
import { FileUpload } from '../../../../components/FileUpload';
import { RAW_NODE_TYPE, TRIGGER_NODE_TYPE, isPlainObject, type JsonObject } from '../mapper';
import { askOptions, kbScope, llmMode, notifyChannels, slug, splitBranches, switchCases, formFields, formSample, decisionPaths, opRight, DECISION_OP_LIST, NOTIFY_CHANNEL_TYPES, VALIDATE_KINDS, FIELD_TYPES, COUNTRY_CODES, HTTP_AUTH_TYPES, type NotifyChannelType, type FieldType, type FormFieldSpec, type HttpAuthType, type DecisionPath, triggerIntent, intentAllowed, intentEnabled, INTENT_LABEL_MAX, type IntentMode, type TriggerIntent, hoursSource, type HoursSource, bookingChoosesService, bookingStaff, BOOKING_DAYS_DEFAULT, BOOKING_DAYS_MAX, ticketFields, ticketTags, TICKET_DEFAULT_SUBJECT, listMenu, LIST_MAX_OWN } from './catalog';
import { useBookingSetup } from './useBookingSetup';
import { useTicketSetup } from './useTicketSetup';
import { useApiSetup } from './useApiSetup';
import { slug as keyOf } from '../../../knowledge/api-docs';
import type { Translate } from './nodes';
import { HttpAuthFields } from './HttpAuthFields';
import { useT, useI18n } from '../../../../i18n/I18nProvider';
import { languageOptions, spokenLanguages, useLanguages } from '../../../../lib/languages';
import { useBots } from '../../../../lib/bots';
import { phoneLabels } from '../../../../lib/phone';
import { useEmailStatus } from '../../../../lib/email';

export type InspectorProps = { node: FlowNodeData; onChange: (data: JsonObject) => void };
export type Inspector = (props: InspectorProps) => ReactNode;

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';
const dataOf = (node: FlowNodeData): JsonObject => (node.data ?? {}) as JsonObject;

/** Set (or delete when `value === undefined`) one field, keeping every other field. */
function patch(data: JsonObject, key: string, value: unknown): JsonObject {
  const next = { ...data };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

const asStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const asRows = (v: unknown): JsonObject[] => (Array.isArray(v) ? v.filter(isPlainObject) : []);

// ── Shared pieces ─────────────────────────────────────────────────────────

/**
 * Raw JSON editor for a node's fields. Explicit Apply (not live) so half-typed
 * JSON never corrupts the graph; resets whenever the fields change elsewhere.
 */
function JsonField({
  value, onApply, label, t, requireObject = true
}: {
  value: unknown;
  onApply: (parsed: unknown) => void;
  label: ReactNode;
  t: Translate;
  requireObject?: boolean;
}): ReactNode {
  const pretty = JSON.stringify(value ?? {}, null, 2);
  const [draft, setDraft] = useState(pretty);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setDraft(pretty); setError(null); }, [pretty]);
  const dirty = draft !== pretty;

  function apply() {
    try {
      const parsed: unknown = JSON.parse(draft);
      if (requireObject && !isPlainObject(parsed)) {
        setError(t('flows.builder.jsonObjectRequired'));
        return;
      }
      setError(null);
      onApply(parsed);
    } catch {
      setError(t('flows.builder.invalidJson'));
    }
  }

  return (
    <details className="rr-inspector__advanced">
      <summary>{label}</summary>
      <Stack gap={6} style={{ paddingTop: 8 }}>
        <Textarea
          className="rr-flow-builder__json"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          minRows={6}
          error={error ?? undefined}
          aria-label={typeof label === 'string' ? label : 'JSON'}
        />
        <Group justify="flex-end" gap="xs">
          <Button size="xs" variant="default" disabled={!dirty} onClick={() => { setDraft(pretty); setError(null); }}>
            {t('action.cancel')}
          </Button>
          <Button size="xs" disabled={!dirty} onClick={apply}>
            {t('flows.builder.applyJson')}
          </Button>
        </Group>
      </Stack>
    </details>
  );
}

/** Name and note: what this node is, in the author's words. On every node. */
function NameFields({ d, onChange, t }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate }) {
  return (
    <>
      <TextInput
        label={t('flows.builder.field.name')}
        description={t('flows.builder.field.nameHint')}
        value={asStr(d.name)}
        onChange={(e) => onChange(patch(d, 'name', e.currentTarget.value || undefined))}
      />
      <Textarea
        label={t('flows.builder.field.description')}
        description={t('flows.builder.field.descriptionHint')}
        value={asStr(d.description)}
        minRows={2}
        onChange={(e) => onChange(patch(d, 'description', e.currentTarget.value || undefined))}
      />
    </>
  );
}

/** Variables the author can put in a template, as a hint under a field. */
function varHint(variables: string[], t: Translate): string {
  if (variables.length === 0) return t('flows.builder.field.templateHint');
  const shown = variables.slice(0, 6).map((v) => `{{${v}}}`).join(' ');
  return `${t('flows.builder.field.templateHint')} ${shown}${variables.length > 6 ? ' …' : ''}`;
}

/** Rows of two text fields (key / value), with add and remove. */
function PairRows({
  rows, onChange, keyLabel, valueLabel, addLabel, keyPlaceholder, valuePlaceholder, keyList, valueTokens, fallbackHint, t
}: {
  rows: Array<{ k: string; v: string }>;
  onChange: (rows: Array<{ k: string; v: string }>) => void;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  /** A `<datalist>` id of known keys, so a builder picks an existing name rather than inventing a near-duplicate. */
  keyList?: string;
  /** When given, the value field offers these on `{{` and paints the ones it holds. */
  valueTokens?: TokenSuggestion[];
  fallbackHint?: string;
  t: Translate;
}) {
  return (
    <Stack gap={6}>
      {rows.map((row, i) => (
        <div key={i} className="rr-inspector__row">
          <TextInput
            aria-label={keyLabel}
            placeholder={keyPlaceholder ?? keyLabel}
            list={keyList}
            value={row.k}
            onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, k: e.currentTarget.value } : r)))}
          />
          {valueTokens ? (
            <TokenInput
              aria-label={valueLabel}
              placeholder={valuePlaceholder ?? valueLabel}
              tokens={valueTokens}
              fallbackHint={fallbackHint}
              value={row.v}
              onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, v: e.currentTarget.value } : r)))}
            />
          ) : (
            <TextInput
              aria-label={valueLabel}
              placeholder={valuePlaceholder ?? valueLabel}
              value={row.v}
              onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, v: e.currentTarget.value } : r)))}
            />
          )}
          <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <CloseIcon />
          </ActionIcon>
        </div>
      ))}
      <Button size="xs" variant="subtle" onClick={() => onChange([...rows, { k: '', v: '' }])}>+ {addLabel}</Button>
    </Stack>
  );
}

/**
 * A dropdown of the known values with a way out: "Type my own…" turns it
 * into a text field, and a value the list does not know starts there. Used
 * wherever a variable or a response field is wanted, so nobody has to
 * remember a name they could pick.
 */
function PickOrType({ label, description, ariaLabel, value, options, onChange, placeholder, tokens, fallbackHint, t }: {
  label?: string;
  description?: string;
  ariaLabel?: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Offered on `{{` while typing, for a field that holds a template rather than a bare name. */
  tokens?: TokenSuggestion[];
  fallbackHint?: string;
  t: Translate;
}) {
  const OWN = '__own__';
  const known = value === '' || options.some((o) => o.value === value);
  const [typing, setTyping] = useState(!known);
  useEffect(() => { if (!known) setTyping(true); }, [known]);
  if (typing || options.length === 0) {
    return (
      <div className="rr-pick-or-type">
        {tokens ? (
          <TokenInput label={label} description={description} aria-label={ariaLabel ?? label} value={value} placeholder={placeholder} tokens={tokens} fallbackHint={fallbackHint} onChange={(e) => onChange(e.currentTarget.value)} />
        ) : (
          <TextInput label={label} description={description} aria-label={ariaLabel ?? label} value={value} placeholder={placeholder} onChange={(e) => onChange(e.currentTarget.value)} />
        )}
        {options.length > 0 && (
          <button type="button" className="rr-pick-or-type__back" onClick={() => { setTyping(false); if (!known) onChange(''); }}>
            {t('flows.builder.pickOrType.back')}
          </button>
        )}
      </div>
    );
  }
  return (
    <Select
      label={label}
      description={description}
      aria-label={ariaLabel ?? label}
      value={value}
      onChange={(e) => { const v = e.currentTarget.value; if (v === OWN) { setTyping(true); return; } onChange(v); }}
      data={[{ value: '', label: t('flows.builder.pickOrType.pick') }, ...options, { value: OWN, label: t('flows.builder.pickOrType.own') }]}
    />
  );
}

/** The known variables as dropdown rows; `wrap` gives them as {{name}} for template fields. */
const varOptions = (variables: string[], wrap = false) => variables.map((v) => ({ value: wrap ? `{{${v}}}` : v, label: wrap ? `{{${v}}}` : v }));
const COMMON_HEADERS = ['Authorization', 'X-API-Key', 'Accept', 'Content-Type', 'User-Agent', 'X-Request-Id', 'X-Tenant', 'Cookie'];
const RETRY_DEFAULT_KINDS = new Set(['name', 'email', 'phone', 'number', 'integer', 'date', 'time', 'yes_no', 'id_number', 'url', 'postcode', 'text', 'choice']);

/** The retry hint: the built-in line the bot uses for this rule, or the general note. */
function retryHint(kind: string, t: Translate): string {
  return RETRY_DEFAULT_KINDS.has(kind) ? t('flows.builder.field.retryDefaultHint', { text: t(`flows.builder.retryDefault.${kind}`) }) : t('flows.builder.field.retryMessageHint');
}

/**
 * What an answer must be, and the details that go with the rule: the pattern
 * for a regex, the country for a phone number or a postal code, the length
 * limits for text. Shared by the ask step and every form field.
 */
function ValidationFields({ d, onChange, t, kinds = VALIDATE_KINDS, keyName = 'validate' }: {
  d: JsonObject; onChange: (next: JsonObject) => void; t: Translate; kinds?: readonly string[]; keyName?: 'validate' | 'type';
}) {
  const raw = asStr(d[keyName]);
  const kind = kinds.includes(raw) ? raw : keyName === 'validate' ? 'none' : kinds[0];
  return (
    <>
      <Select
        label={t(keyName === 'validate' ? 'flows.builder.field.validate' : 'flows.builder.field.fieldType')}
        description={t(`flows.builder.validateDesc.${kind}`)}
        value={kind}
        onChange={(e) => onChange(patch(d, keyName, keyName === 'validate' && e.currentTarget.value === 'none' ? undefined : e.currentTarget.value))}
        data={kinds.map((v) => ({ value: v, label: t(`flows.builder.validate.${v}`) }))}
      />
      {kind === 'regex' && (
        <TextInput
          label={t('flows.builder.field.pattern')}
          value={asStr(d.pattern)}
          placeholder="^[A-Z]{2}\\d{6}$"
          onChange={(e) => onChange(patch(d, 'pattern', e.currentTarget.value || undefined))}
        />
      )}
      {(kind === 'phone' || kind === 'postcode') && (
        <Select
          label={t('flows.builder.field.country')}
          description={t('flows.builder.field.countryHint')}
          value={asStr(d.country) || 'ZA'}
          onChange={(e) => onChange(patch(d, 'country', e.currentTarget.value === 'ZA' ? undefined : e.currentTarget.value))}
          data={COUNTRY_CODES.map((c) => ({ value: c, label: t(`flows.builder.country.${c}`) }))}
        />
      )}
      {kind === 'text' && (
        <div className="rr-inspector__row rr-inspector__row--pair">
          <NumberInput label={t('flows.builder.field.minLength')} value={typeof d.min_length === 'number' ? d.min_length : ''} min={1} max={4000} onChange={(v) => onChange(patch(d, 'min_length', v === '' ? undefined : v))} />
          <NumberInput label={t('flows.builder.field.maxLength')} value={typeof d.max_length === 'number' ? d.max_length : ''} min={1} max={4000} onChange={(v) => onChange(patch(d, 'max_length', v === '' ? undefined : v))} />
        </div>
      )}
    </>
  );
}

/** "What is your phone number?" → "phone_number": a variable name from a question, for a form field. */
function autoName(question: string): string {
  const stop = new Set(['what', 'is', 'your', 'the', 'a', 'an', 'which', 'please', 'you', 'do', 'are', 'how', 'many', 'me', 'tell', 'can', 'we', 'of', 'to', 'for', 'and', 'or', 'would', 'like', 'have', 'any', 'in', 'on', 'be', 'it', 'us', 'give', 'send', 'when', 'where', 'that', 'this', 'so', 'much']);
  return question.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter((w) => w && !stop.has(w)).slice(0, 2).join('_');
}

/** The {{variables}} an HTTP step's fields mention, for the sample values of a test request. */
function usedVariables(d: JsonObject): string[] {
  const saveAs = typeof d.save_as === 'string' && d.save_as ? d.save_as : 'http';
  const text = JSON.stringify({ url: d.url, headers: d.headers, body: d.body, auth: d.auth, reply: isPlainObject(d.reply) ? d.reply.text : undefined });
  const names = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)/g)) {
    const name = m[1];
    if (name === saveAs || name.startsWith(`${saveAs}.`) || name === 'now') continue;
    names.add(name);
  }
  return [...names];
}

/** Typed sample values as the object the engine gets: dotted names nest, JSON text becomes an object. */
function sampleObject(sample: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, raw] of Object.entries(sample)) {
    let value: unknown = raw;
    const trimmed = raw.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try { value = JSON.parse(trimmed); } catch { value = raw; }
    }
    const parts = name.split('.');
    let cur: Record<string, unknown> = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const next = cur[parts[i]];
      if (!isPlainObject(next)) cur[parts[i]] = {};
      cur = cur[parts[i]] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return out;
}

/** "data.items[0].status" → "status": a variable name for a kept response field. */
function lastSegment(path: string): string {
  const seg = path.replace(/\[\d+\]/g, '').split('.').filter(Boolean).pop() ?? 'value';
  const name = seg.replace(/[^A-Za-z0-9_]/g, '_');
  return name === 'body' || name === '$' ? 'value' : name;
}

/** The choices an ask (or a send) offers: title per row, id derived unless typed. */
/**
 * "Keep the answer for" (19 October 2026): a read's answer reused for a while, by every customer whose request is the
 * same, or within one conversation. `post` offers "This request only reads" for a POST (a search).
 */
function CacheFields({ d, onChange, t, method }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate; method?: string }) {
  const cache: JsonObject | null = isPlainObject(d.cache) ? d.cache : null;
  const minutes = cache && typeof cache.minutes === 'number' ? String(cache.minutes) : '';
  const set = (changes: JsonObject | undefined) => onChange(patch(d, 'cache', changes));
  const writes = method !== undefined && !['GET', 'POST'].includes(method);
  return (
    <Stack gap={6} data-testid="cache-fields">
      <Select
        label={t('flows.builder.cache.keep')}
        description={writes ? t('flows.builder.cache.writes') : t('flows.builder.cache.keepHint')}
        value={minutes}
        disabled={writes}
        onChange={(e) => set(e.currentTarget.value ? { ...(cache ?? {}), minutes: Number(e.currentTarget.value) } : undefined)}
        data={[{ value: '', label: t('flows.builder.cache.none') }, ...flows.CACHE_MINUTES.map((m) => ({ value: String(m), label: t(`flows.builder.cache.m${m}`) }))]}
      />
      {cache && !writes && (
        <Select
          label={t('flows.builder.cache.share')}
          value={cache.share === 'conversation' ? 'conversation' : 'everyone'}
          onChange={(e) => set({ ...cache, share: e.currentTarget.value === 'conversation' ? 'conversation' : undefined })}
          data={[{ value: 'everyone', label: t('flows.builder.cache.everyone') }, { value: 'conversation', label: t('flows.builder.cache.conversation') }]}
        />
      )}
      {cache && method === 'POST' && (
        <Switch label={t('flows.builder.cache.read')} description={t('flows.builder.cache.readHint')} checked={cache.read === true}
          onChange={(on) => set({ ...cache, read: on || undefined })} />
      )}
    </Stack>
  );
}

const LIST_OPS = ['eq', 'neq', 'contains', 'starts_with', 'on_date', 'after', 'before', 'gte', 'lte', 'filled'] as const;

/**
 * A menu built from a list (19 October 2026): the list, what each row shows and keeps, and which items to offer.
 * The rest of the step (its own options, the variable) stays as it is.
 */
function ListMenuFields({ d, onChange, t, tokens, hint }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate; tokens: TokenSuggestion[]; hint: string }) {
  const [on, setOn] = useState(listMenu(d));
  const rule: JsonObject = Array.isArray(d.option_filter) && isPlainObject(d.option_filter[0]) ? (d.option_filter[0] as JsonObject) : {};
  const setRule = (changes: Record<string, string>) => {
    const next: JsonObject = { ...rule, ...changes };
    onChange(patch(d, 'option_filter', asStr(next.left).trim() ? [next] : undefined));
  };
  return (
    <Stack gap={6} data-testid="list-menu-fields">
      <Switch label={t('flows.builder.list.use')} description={t('flows.builder.list.useHint')} checked={on}
        onChange={(checked) => {
          setOn(checked);
          if (!checked) onChange(patch(patch(patch(patch(d, 'options_from', undefined), 'option_title', undefined), 'option_value', undefined), 'option_filter', undefined));
        }} />
      {on && (
        <>
          <TokenInput tokens={tokens} fallbackHint={hint} label={t('flows.builder.list.from')} description={t('flows.builder.list.fromHint')}
            value={asStr(d.options_from)} placeholder={'{{tour_slots.items}}' /* i18n-ignore: variable syntax, not words */} data-testid="list-from"
            onChange={(e) => onChange(patch(d, 'options_from', e.currentTarget.value || undefined))} />
          <TokenInput tokens={tokens} fallbackHint={hint} label={t('flows.builder.list.title')} description={t('flows.builder.list.titleHint')}
            value={asStr(d.option_title)} placeholder={'{{item.title}}' /* i18n-ignore: variable syntax, not words */}
            onChange={(e) => onChange(patch(d, 'option_title', e.currentTarget.value || undefined))} />
          <TokenInput tokens={tokens} fallbackHint={hint} label={t('flows.builder.list.value')} description={t('flows.builder.list.valueHint')}
            value={asStr(d.option_value)} placeholder={'{{item.id}}' /* i18n-ignore: variable syntax, not words */}
            onChange={(e) => onChange(patch(d, 'option_value', e.currentTarget.value || undefined))} />
          <Text role="inputLabel">{t('flows.builder.list.filter')}</Text>
          <div className="rr-inspector__row rr-inspector__row--rule">
            <TextInput aria-label={t('flows.builder.list.filterField')} placeholder="item.date" value={asStr(rule.left)}
              onChange={(e) => setRule({ left: e.currentTarget.value })} />
            <Select aria-label={t('flows.builder.list.filterOp')} value={asStr(rule.op) || 'eq'}
              onChange={(e) => setRule({ op: e.currentTarget.value })}
              data={LIST_OPS.map((op) => ({ value: op, label: t(`flows.builder.list.op.${op}`) }))} />
            <TokenInput tokens={tokens} fallbackHint={hint} aria-label={t('flows.builder.list.filterValue')} placeholder="{{preferred_date}}" value={asStr(rule.right)}
              onChange={(e) => setRule({ right: e.currentTarget.value })} />
          </div>
          <Text role="caption" c={MUTED}>{t('flows.builder.list.filterHint')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.list.kept', { variable: asStr(d.variable) || 'answer', max: LIST_MAX_OWN })}</Text>
        </>
      )}
    </Stack>
  );
}

function OptionsEditor({ d, onChange, t }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate }) {
  const rows = asRows(d.options).map((o) => ({ id: asStr(o.id), title: asStr(o.title), description: asStr(o.description) }));
  const write = (next: typeof rows) => onChange(patch(d, 'options', next.length ? next.map((r, i) => ({
    id: r.id.trim() || slug(r.title, i + 1),
    title: r.title,
    ...(r.description.trim() ? { description: r.description } : {})
  })) : undefined));
  return (
    <Stack gap={6}>
      <Text role="inputLabel">{t('flows.builder.field.options')}</Text>
      <Text role="caption" c={MUTED}>{t('flows.builder.field.optionsHint')}</Text>
      {rows.map((row, i) => (
        <div key={i} className="rr-inspector__row rr-inspector__row--option">
          <TextInput
            aria-label={t('flows.builder.field.optionTitle')}
            placeholder={t('flows.builder.field.optionTitle')}
            value={row.title}
            onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, title: e.currentTarget.value } : r)))}
          />
          <TextInput
            aria-label={t('flows.builder.field.optionId')}
            placeholder={t('flows.builder.field.optionId')}
            value={row.id}
            onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, id: e.currentTarget.value } : r)))}
          />
          <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(rows.filter((_, j) => j !== i))}>
            <CloseIcon />
          </ActionIcon>
        </div>
      ))}
      <Button size="xs" variant="subtle" disabled={rows.length >= 10} onClick={() => write([...rows, { id: '', title: '', description: '' }])}>
        + {t('flows.builder.field.addOption')}
      </Button>
      {rows.length > 3 && <Text role="caption" c={MUTED}>{t('flows.builder.field.optionsListHint')}</Text>}
    </Stack>
  );
}

/** Media on a send or an ask: picked from the media library, stored as a signed URL. */
function MediaFields({ d, onChange, t }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate }) {
  return (
    <Stack gap={6}>
      <FileUpload
        label={t('flows.builder.field.media')}
        description={t('flows.builder.field.mediaHint')}
        accept={['image/jpeg', 'image/png', 'video/mp4', 'application/pdf']}
        value={asStr(d.media_url) || null}
        onChange={(next) => onChange(patch(patch(d, 'media_url', next?.url || undefined), 'media_type', next ? kindOfMime(next.mime_type) : undefined))}
      />
      {asStr(d.media_url) && (
        <TextInput
          label={t('flows.builder.field.caption')}
          value={asStr(d.caption)}
          onChange={(e) => onChange(patch(d, 'caption', e.currentTarget.value || undefined))}
        />
      )}
    </Stack>
  );
}

function kindOfMime(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

/**
 * Knowledge-base documents, by name. Falls back to the raw id list if the
 * documents cannot be loaded: an unreachable KB service must not make an
 * existing selection uneditable.
 */
function KbPicker({ selected, onChange, fallbackText }: { selected: string[]; onChange: (ids: string[]) => void; fallbackText: string }) {
  const tt = useT();
  const docs = useQuery({ queryKey: ['kb-documents'], queryFn: () => flows.listKbDocuments(), staleTime: 60_000, retry: false });

  if (docs.isError) {
    return (
      <Textarea
        label={tt('flows.builder.field.kbIds')}
        description={tt('flows.builder.field.kbIdsUnavailable')}
        value={fallbackText}
        minRows={3}
        onChange={(e) => onChange(e.currentTarget.value.split(/\r?\n/).map((x) => x.trim()).filter(Boolean))}
      />
    );
  }
  const available = docs.data?.data ?? [];
  return (
    <Stack gap={4}>
      <Text role="inputLabel">{tt('flows.builder.field.kbIds')}</Text>
      <Text role="caption" c={MUTED}>{tt('flows.builder.field.kbIdsHint2')}</Text>
      {docs.isPending && <Skeleton height={60} />}
      {!docs.isPending && available.length === 0 && <Text role="caption" c={MUTED}>{tt('flows.builder.field.kbEmpty')}</Text>}
      <Stack gap={4}>
        {available.map((doc) => (
          <Switch
            key={doc.id}
            label={doc.name || doc.id}
            checked={selected.includes(doc.id)}
            onChange={() => onChange(selected.includes(doc.id) ? selected.filter((x) => x !== doc.id) : [...selected, doc.id])}
          />
        ))}
      </Stack>
      {selected.filter((id) => !available.some((doc) => doc.id === id)).map((id) => (
        <Group key={id} gap={6} align="center">
          <Text role="caption" c={MUTED}>{tt('flows.builder.field.kbUnknown', { id })}</Text>
          <Button size="xs" variant="subtle" onClick={() => onChange(selected.filter((x) => x !== id))}>{tt('action.remove')}</Button>
        </Group>
      ))}
    </Stack>
  );
}

/** Upload a document here and it is added to the node's list, no trip to the knowledge base needed. */
function KbUpload({ onIngested }: { onIngested: (id: string) => void }) {
  const tt = useT();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Stack gap={4}>
      <Text role="inputLabel">{tt('flows.builder.field.kbUpload')}</Text>
      <Text role="caption" c={MUTED}>{tt('flows.builder.field.kbUploadHint')}</Text>
      <FileUpload
        accept={['application/pdf', 'text/plain', 'text/markdown', 'text/html', 'text/csv', '.pdf', '.txt', '.md', '.html', '.csv']}
        value={null}
        withLibrary={false}
        onChange={async (next) => {
          if (!next) return;
          setBusy(true);
          setError(null);
          try {
            const res = await flows.ingestKbFromMedia(next.id, next.original_name ?? undefined);
            qc.invalidateQueries({ queryKey: ['kb-documents'] });
            onIngested(res.data.id);
          } catch (e) {
            setError(e instanceof Error ? e.message : tt('common.unknownError'));
          } finally {
            setBusy(false);
          }
        }}
      />
      {busy && <Text role="caption" c={MUTED}>{tt('knowledge.add.reading')}</Text>}
      {error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{error}</Text>}
      <Text role="caption" c={MUTED}>
        {tt('flows.builder.field.kbManage')} <Link to="/knowledge">{tt('nav.knowledge')}</Link>
      </Text>
    </Stack>
  );
}

/** Rows of id + title: the labels an AI sorts messages into, or the fields it pulls out. */
function NamedItemsEditor({
  d, onChange, field, label, hint, idLabel, titleLabel, addLabel, idPlaceholder, titlePlaceholder, t
}: {
  d: JsonObject; onChange: (next: JsonObject) => void; field: 'labels' | 'fields';
  label: string; hint: string; idLabel: string; titleLabel: string; addLabel: string; idPlaceholder: string; titlePlaceholder: string; t: Translate;
}) {
  const rows = asRows(d[field]).map((o) => ({ id: asStr(o.id ?? o.name), title: asStr(o.title ?? o.description) }));
  const write = (next: typeof rows) => onChange(patch(d, field, next.length
    ? next.map((r, i) => (field === 'labels'
      ? { id: r.id.trim() || slug(r.title, i + 1), title: r.title || r.id }
      : { name: (r.id.trim() || slug(r.title, i + 1)).replace(/[^A-Za-z0-9_]/g, '_'), description: r.title || r.id }))
    : undefined));
  return (
    <Stack gap={6}>
      <Text role="inputLabel">{label}</Text>
      <Text role="caption" c={MUTED}>{hint}</Text>
      {rows.map((row, i) => (
        <div key={i} className="rr-inspector__row rr-inspector__row--option">
          <TextInput
            aria-label={idLabel}
            placeholder={idPlaceholder}
            value={row.id}
            onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, id: e.currentTarget.value } : r)))}
          />
          <TextInput
            aria-label={titleLabel}
            placeholder={titlePlaceholder}
            value={row.title}
            onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, title: e.currentTarget.value } : r)))}
          />
          <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(rows.filter((_, j) => j !== i))}>
            <CloseIcon />
          </ActionIcon>
        </div>
      ))}
      <Button size="xs" variant="subtle" onClick={() => write([...rows, { id: '', title: '' }])}>+ {addLabel}</Button>
    </Stack>
  );
}

const OPS = ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'gt', 'gte', 'lt', 'lte', 'in', 'exists', 'not_exists', 'empty', 'not_empty', 'matches',
  'filled', 'not_filled', 'is_true', 'is_false', 'is_today', 'on_date', 'before', 'after', 'on_or_before', 'on_or_after', 'within_days', 'within_last_days', 'older_than_days'] as const;
const UNARY = new Set(['exists', 'not_exists', 'empty', 'not_empty', 'filled', 'not_filled', 'is_true', 'is_false', 'is_today']);

/** The conditions of a Condition node: variable, operator, value; all or any. */
function RulesEditor({ d, onChange, variables, t }: { d: JsonObject; onChange: (next: JsonObject) => void; variables: string[]; t: Translate }) {
  const rows = asRows(d.rules).map((r) => ({ left: asStr(r.left), op: asStr(r.op) || 'eq', right: asStr(r.right) }));
  const write = (next: typeof rows) => onChange(patch(d, 'rules', next));
  return (
    <Stack gap={8}>
      <Select
        label={t('flows.builder.field.match')}
        value={asStr(d.match) === 'any' ? 'any' : 'all'}
        onChange={(e) => onChange(patch(d, 'match', e.currentTarget.value))}
        data={[{ value: 'all', label: t('flows.builder.field.matchAll') }, { value: 'any', label: t('flows.builder.field.matchAny') }]}
      />
      <Text role="inputLabel">{t('flows.builder.field.rules')}</Text>
      <Text role="caption" c={MUTED}>{varHint(variables, t)}</Text>
      {rows.map((row, i) => (
        <div key={i} className="rr-inspector__rule">
          <PickOrType
            ariaLabel={t('flows.builder.field.ruleLeft')}
            placeholder={t('flows.builder.field.ruleLeft')}
            value={row.left}
            options={varOptions(variables)}
            onChange={(v) => write(rows.map((r, j) => (j === i ? { ...r, left: v } : r)))}
            t={t}
          />
          <Select
            aria-label={t('flows.builder.field.ruleOp')}
            value={row.op}
            onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, op: e.currentTarget.value } : r)))}
            data={OPS.map((op) => ({ value: op, label: t(`flows.builder.op.${op}`) }))}
          />
          {!UNARY.has(row.op) && (
            <TextInput
              aria-label={t('flows.builder.field.ruleRight')}
              placeholder={t('flows.builder.field.ruleRight')}
              value={row.right}
              onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, right: e.currentTarget.value } : r)))}
            />
          )}
          <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(rows.filter((_, j) => j !== i))}>
            <CloseIcon />
          </ActionIcon>
        </div>
      ))}
      <Button size="xs" variant="subtle" onClick={() => write([...rows, { left: '', op: 'eq', right: '' }])}>+ {t('flows.builder.field.addRule')}</Button>
      <datalist id="rr-flow-variables">{variables.map((v) => <option key={v} value={v} />)}</datalist>
    </Stack>
  );
}

/** Which heading a variable sits under in a Decision rule's picker. */
type VarGroup = 'customer' | 'answers' | 'api' | 'clock' | 'ai' | 'other';
const VAR_GROUP_ORDER: VarGroup[] = ['customer', 'answers', 'api', 'clock', 'ai', 'other'];
function varGroup(name: string): VarGroup {
  if (name.startsWith('contact.')) return 'customer';
  if (name.startsWith('clock.') || name === 'now' || name.startsWith('business_hours.')) return 'clock';
  if (/\.(ok|status|body)$/.test(name)) return 'api';
  if (/\.(text|confidence|label)$/.test(name)) return 'ai';
  if (['message', 'trigger.text', 'channel'].includes(name) || name.startsWith('bot.')) return 'other';
  return 'answers';
}

/**
 * What a Decision rule can look at, as one dropdown: the customer's details
 * (including every contact field the organisation has), the answers and saved
 * values of this chat, what a Web API step returned, the date and time, and
 * what an AI step concluded. The dropdown has no section headings, so each
 * row carries its section as a prefix and the rows are sorted by section.
 */
function ruleLeftOptions(variables: string[], fieldKeys: string[], t: Translate): Array<{ value: string; label: string }> {
  const names = new Set(variables);
  for (const k of fieldKeys) names.add(`contact.fields.${k}`);
  return [...names]
    .map((name) => ({ name, group: varGroup(name) }))
    .sort((a, b) => VAR_GROUP_ORDER.indexOf(a.group) - VAR_GROUP_ORDER.indexOf(b.group) || a.name.localeCompare(b.name))
    .map(({ name, group }) => ({ value: name, label: `${t(`flows.builder.varGroup.${group}`)}: ${name}` }));
}

function ArrowIcon({ up }: { up: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={up ? 'M8 12.5v-9M4.2 7.2 8 3.4l3.8 3.8' : 'M8 3.5v9M4.2 8.8 8 12.6l3.8-3.8'} />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <path d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6" />
    </svg>
  );
}

// ── What the flow is for ──────────────────────────────────────────────────

/** What "Try a message" found, in one or two sentences. */
export function intentResultText(res: flows.IntentTestResult, t: Translate): string {
  const pct = (score?: number | null) => Math.round((score ?? 0) * 100);
  const closest = res.candidates[0];
  if (res.action === 'keyword') return t('flows.builder.intent.result.keyword', { flow: res.flow?.name ?? '' });
  if (res.action === 'choose') {
    const names = (res.choices ?? []).map((c) => `“${c.name}”`).join(', ');
    const close = res.reason === 'close_keyword';
    const how = res.choice_mode === 'ask' ? t('flows.builder.intent.result.chooseAsk') : t(close ? 'flows.builder.intent.result.chooseCloseAssistant' : 'flows.builder.intent.result.chooseAssistant');
    return `${t(close ? 'flows.builder.intent.result.chooseClose' : 'flows.builder.intent.result.choose', { flows: names })} ${how}`;
  }
  if (res.action === 'jump') return t('flows.builder.intent.result.jump', { flow: res.flow?.name ?? '', score: pct(res.flow?.score) });
  if (res.action === 'offer') {
    const first = res.offers[0] ?? res.flow;
    const line = t('flows.builder.intent.result.offer', { flow: first?.name ?? '', label: first?.button_label ?? first?.name ?? '', score: pct(first?.score) });
    const why = ['question', 'close_call', 'offer_only', 'likely'].includes(res.reason) ? ` ${t(`flows.builder.intent.reason.${res.reason}`)}` : '';
    return line + why;
  }
  const none = t('flows.builder.intent.result.none');
  if (res.reason === 'no_match') return closest ? `${none} ${t('flows.builder.intent.reason.no_match', { flow: closest.name, score: pct(closest.score) })}` : none;
  const known = ['no_flows', 'greeting', 'small_talk', 'too_short', 'too_long', 'unavailable'].includes(res.reason);
  return known ? `${none} ${t(`flows.builder.intent.reason.${res.reason}`)}` : none;
}

/**
 * "When a customer asks for it": what the flow is for, in the customer's
 * words, so a message that asks for it starts the flow (or brings it along as
 * a button) with no keyword. Stored as `intent` on the trigger. "Try a
 * message" asks the service what a sentence would do; it reads the flows as
 * last saved, which the hint says.
 */
function IntentFields({ d, onChange, t, botId, flowName }: { d: JsonObject; onChange: (next: JsonObject) => void; t: Translate; botId?: string; flowName?: string }) {
  const intent = triggerIntent(d);
  const keywords = triggerKeywords(d);
  const [question, setQuestion] = useState('');
  // "Suggest more" (18 October 2026): new ways customers might ask, from what the flow is for, its keywords and the
  // ways already listed. Charged as AI tokens.
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const listed = intent.examples.map((e) => e.trim()).filter(Boolean);
  const canSuggest = intent.description.trim().length > 0 || keywords.length > 0 || listed.length > 0;
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const write = (next: Partial<TriggerIntent>, tidy = false) => {
    const merged = { ...intent, ...next };
    const examples = tidy ? merged.examples.map((e) => e.trim()).filter(Boolean) : merged.examples;
    const empty = !merged.description.trim() && !examples.some((e) => e.trim()) && !merged.buttonLabel.trim() && merged.mode === 'jump';
    if (empty) { onChange(patch(d, 'intent', undefined)); return; }
    const out: JsonObject = { ...(d.intent && typeof d.intent === 'object' && !Array.isArray(d.intent) ? (d.intent as JsonObject) : {}) };
    out.description = merged.description;
    out.examples = examples;
    out.mode = merged.mode;
    if (merged.buttonLabel.trim()) out.button_label = merged.buttonLabel; else delete out.button_label;
    onChange(patch(d, 'intent', out));
  };

  async function suggest() {
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const res = await flows.suggestIntentPhrases({ description: intent.description.trim() || undefined, keywords, examples: listed, flowName, botId });
      setSuggestions(res.data.suggestions);
      if (res.data.suggestions.length === 0) setSuggestNote(t('flows.builder.intent.suggestNone'));
    } catch (e) {
      setSuggestions([]);
      setSuggestNote(e instanceof Error ? e.message : t('common.unknownError'));
    } finally {
      setSuggesting(false);
    }
  }
  const addPhrases = (phrases: string[]) => {
    write({ examples: [...listed, ...phrases.filter((p) => !listed.some((x) => x.toLowerCase() === p.toLowerCase()))] }, true);
    setSuggestions((current) => current.filter((p) => !phrases.includes(p)));
  };

  async function tryIt() {
    setBusy(true);
    setError(null);
    try {
      const res = await flows.testIntent(question.trim(), { botId });
      setResult(intentResultText(res.data, t));
    } catch (e) {
      setResult(null);
      setError(e instanceof Error ? e.message : t('common.unknownError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rr-inspector__intent" data-testid="trigger-intent">
      <Text role="inputLabel">{t('flows.builder.intent.title')}</Text>
      <Text role="caption" c={MUTED}>{t('flows.builder.intent.intro')}</Text>
      <Textarea
        label={t('flows.builder.intent.description')}
        description={t('flows.builder.intent.descriptionHint')}
        value={intent.description}
        minRows={2}
        maxLength={300}
        onChange={(e) => write({ description: e.currentTarget.value })}
      />
      <Textarea
        label={t('flows.builder.intent.examples')}
        description={t('flows.builder.intent.examplesHint')}
        value={intent.examples.join('\n')}
        minRows={3}
        onChange={(e) => write({ examples: e.currentTarget.value.split('\n') })}
        onBlur={() => write({}, true)}
        data-testid="intent-examples"
      />
      <Stack gap={6} data-testid="intent-suggest-box">
        <Group gap="xs" align="center" wrap="wrap">
          <Button size="xs" variant="default" loading={suggesting} disabled={!canSuggest} onClick={suggest} data-testid="intent-suggest">
            {t('flows.builder.intent.suggest')}
          </Button>
          <Text role="caption" c={MUTED}>{t(canSuggest ? 'flows.builder.intent.suggestHint' : 'flows.builder.intent.suggestNeeds')}</Text>
        </Group>
        {suggestNote && <Text role="caption" c="var(--rr-color-error, #dc2626)" data-testid="intent-suggest-note">{suggestNote}</Text>}
        {suggestions.length > 0 && (
          <Stack gap={4} data-testid="intent-suggestions">
            {suggestions.map((p) => (
              <Group key={p} gap="xs" wrap="nowrap" align="center">
                <Button size="xs" variant="default" onClick={() => addPhrases([p])} aria-label={t('flows.builder.intent.addPhrase', { phrase: p })} data-testid="intent-suggestion-add">+</Button>
                <Text role="bodySmall" style={{ overflowWrap: 'anywhere' }} data-testid="intent-suggestion">{p}</Text>
              </Group>
            ))}
            <Group gap="xs">
              <Button size="xs" variant="light" onClick={() => addPhrases(suggestions)} data-testid="intent-suggest-all">{t('flows.builder.intent.addAll')}</Button>
              <Button size="xs" variant="subtle" onClick={() => setSuggestions([])}>{t('flows.builder.intent.dismiss')}</Button>
            </Group>
          </Stack>
        )}
      </Stack>
      <Select
        label={t('flows.builder.intent.mode')}
        description={t(`flows.builder.intent.modeHint.${intent.mode}`)}
        value={intent.mode}
        onChange={(e) => write({ mode: e.currentTarget.value as IntentMode })}
        data={[
          { value: 'jump', label: t('flows.builder.intent.modeJump') },
          { value: 'offer', label: t('flows.builder.intent.modeOffer') },
          { value: 'off', label: t('flows.builder.intent.modeOff') }
        ]}
      />
      {intent.mode !== 'off' && (
        <TextInput
          label={t('flows.builder.intent.button')}
          description={t('flows.builder.intent.buttonHint', { max: INTENT_LABEL_MAX })}
          value={intent.buttonLabel}
          maxLength={INTENT_LABEL_MAX}
          onChange={(e) => write({ buttonLabel: e.currentTarget.value })}
        />
      )}
      <div className="rr-inspector__probe">
        <Text role="inputLabel">{t('flows.builder.intent.try')}</Text>
        <Text role="caption" c={MUTED}>{t('flows.builder.intent.tryHint')}</Text>
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            aria-label={t('flows.builder.intent.try')}
            placeholder={t('flows.builder.intent.tryPlaceholder')}
            value={question}
            maxLength={400}
            onChange={(e) => setQuestion(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && question.trim() && !busy) { e.preventDefault(); void tryIt(); } }}
            style={{ flex: 1 }}
          />
          <Button size="xs" variant="default" loading={busy} disabled={!question.trim()} onClick={tryIt}>{t('flows.builder.intent.tryRun')}</Button>
        </Group>
        {error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{error}</Text>}
        {result && <Text role="bodySmall" data-testid="intent-result">{result}</Text>}
      </div>
    </div>
  );
}

/** The keywords a trigger lists, whichever field holds them. */
function triggerKeywords(d: JsonObject): string[] {
  return Array.isArray(d.keywords) ? d.keywords.map(String).map((k) => k.trim()).filter(Boolean) : asStr(d.keyword).trim() ? [asStr(d.keyword).trim()] : [];
}

/**
 * Keywords another flow of the same chatbot also uses (18 October 2026): the same once tidied, one inside the other
 * ("tour" and "book a tour"), or a close spelling. Saying so here is the point: when a message matches more than one,
 * the assistant chooses using what each flow is for, or the customer is asked, and a clear "When a customer asks for
 * it" on each flow is what makes the choice right. Checked against the chatbot's active flows as saved.
 */
function KeywordClashes({ keywords, flowId, botId, hasIntent, t }: { keywords: string[]; flowId?: string; botId?: string; hasIntent: boolean; t: Translate }) {
  const [overlaps, setOverlaps] = useState<flows.KeywordOverlap[]>([]);
  const key = keywords.join('\u0001');
  useEffect(() => {
    if (keywords.length === 0) { setOverlaps([]); return; }
    let live = true;
    const timer = setTimeout(() => {
      flows.checkKeywords(keywords, { flowId, botId })
        .then((res) => { if (live) setOverlaps(res.data.overlaps); })
        .catch(() => { if (live) setOverlaps([]); });
    }, 500);
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, flowId, botId]);
  if (overlaps.length === 0) return null;
  const needIntent = !hasIntent || overlaps.some((o) => !o.has_intent);
  return (
    <Alert color="warning" data-testid="keyword-clash">
      <Stack gap={4}>
        {overlaps.map((o, i) => (
          <Text key={`${o.flow_id}-${o.keyword}-${o.other_keyword}-${i}`} role="bodySmall">
            {t(`flows.builder.keywordClash.${o.kind}`, { keyword: o.keyword, other: o.other_keyword, flow: o.flow_name })}{' '}
            <Link to={`/flows/${o.flow_id}/builder`}>{t('flows.builder.keywordClash.open')}</Link>
          </Text>
        ))}
        <Text role="caption">{t('flows.builder.keywordClash.how')}</Text>
        {needIntent && <Text role="caption">{t('flows.builder.keywordClash.tip')}</Text>}
      </Stack>
    </Alert>
  );
}

// ── Inspectors ────────────────────────────────────────────────────────────

/** A form step earlier in the flow, for an HTTP step that sends its answers. */
export interface FormStep { id: string; name: string; saveAs: string }

/**
 * The languages the flow's chatbot speaks (3 November 2026), for the Change language step: each in its own name, from
 * the chatbot list the console already holds, so the step shows what a customer will be offered. A flow that names no
 * chatbot belongs to the default one. With one language the step has nothing to offer, which is said with a link to
 * where more are chosen; when the chatbot cannot be found, only where the languages are set is said.
 */
function ChatbotLanguages({ botId, t }: { botId?: string; t: Translate }) {
  const { bots, defaultBot, isPending } = useBots();
  const { languages } = useLanguages();
  const bot = botId ? bots.find((b) => b.id === botId) ?? null : defaultBot;
  const settings = bot ? `/settings/assistant/${bot.id}` : '/settings/assistant';
  if (isPending) return <Skeleton height={44} />;
  if (!bot) {
    return (
      <Text role="caption" c={MUTED} data-testid="language-step-where">
        {t('flows.builder.language.where')} <Link to={settings}>{t('flows.builder.language.openSettings')}</Link>
      </Text>
    );
  }
  const spoken = spokenLanguages(bot, languages);
  const nameOf = (code: string) => languages.find((l) => l.code === code)?.native_name ?? code;
  if (spoken.length < 2) {
    return (
      <Alert color="warning" data-testid="language-step-one">
        {t('flows.builder.language.oneLanguage', { language: nameOf(spoken[0]) })} <Link to={settings}>{t('flows.builder.language.chooseMore')}</Link>
      </Alert>
    );
  }
  return (
    <Stack gap={4} data-testid="language-step-languages">
      <Text role="inputLabel">{t('flows.builder.language.offered')}</Text>
      {/* Plain text, not badges: a badge's capitals would turn "isiZulu" into "ISIZULU". */}
      <Text role="bodySmall" fw={600}>{spoken.map(nameOf).join(', ')}</Text>
      <Text role="caption" c={MUTED}>
        {t('flows.builder.language.offeredHint')} <Link to={settings}>{t('flows.builder.language.change')}</Link>
      </Text>
    </Stack>
  );
}

/** One form per node kind. `variables` are what earlier nodes can have set; `forms` the form steps an HTTP body can send. */
/**
 * What the attribute picker offers inside a flow, beyond the run's own
 * variables: the contact's fields (base and custom), and the custom field
 * keys the Update contact node may write to.
 */
export interface FlowAttributes {
  contact: TokenSuggestion[];
  fieldKeys: string[];
  /** The chatbot the flow belongs to: "Try a message" routes among that chatbot's flows. */
  botId?: string;
  /** The flow being edited: left out of the shared-keyword check, and named to "Suggest more". */
  flowId?: string;
  flowName?: string;
}

/** The `<datalist>` the Update contact node's key inputs read from. */
const CONTACT_FIELD_KEYS_LIST = 'rr-contact-field-keys';

/**
 * The run's variables and the contact's fields as one list for `{{`.
 * Variables first, since inside a flow they are what an author reaches for;
 * a variable that names a contact field is listed once, under Contact.
 */
function flowTokens(variables: string[], contact: TokenSuggestion[], t: Translate): TokenSuggestion[] {
  const known = new Set(contact.map((c) => c.token));
  const own = variables.filter((v) => v && !known.has(v) && !v.startsWith('contact.'))
    .map((v) => ({ token: v, label: v, group: t('attributes.group.flow') }));
  const extraContact = variables.filter((v) => v.startsWith('contact.') && !known.has(v))
    .map((v) => ({ token: v, label: v, group: t('attributes.group.contact') }));
  return [...own, ...contact, ...extraContact];
}

export function buildInspectors(t: Translate, variables: string[], forms: FormStep[] = [], attributes?: FlowAttributes): Record<string, Inspector> {
  const tokens = flowTokens(variables, attributes?.contact ?? [], t);
  const hint = t('attributes.flowFallbackHint');
  const fieldKeys = attributes?.fieldKeys ?? [];
  const Trigger: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const kind = ['always', 'webhook', 'message', 'first_contact', 'greeting', 'default', 'post_resolution'].includes(asStr(d.type)) ? asStr(d.type) : 'keyword';
    const keywords = triggerKeywords(d);
    const setKeywords = (raw: string) => {
      const list = raw.split(',').map((k) => k.trim()).filter(Boolean);
      const next: JsonObject = { ...d };
      delete next.keywords;
      next.keyword = list[0] ?? '';
      if (list.length > 1) next.keywords = list;
      onChange(next);
    };
    return (
      <Stack gap="md">
        <Select
          label={t('flows.builder.field.triggerType')}
          description={t('flows.builder.field.triggerTypeHint')}
          value={kind}
          onChange={(e) => onChange(patch(d, 'type', e.currentTarget.value))}
          data={[
            { value: 'keyword', label: t('flows.builder.field.keywordMatch') },
            { value: 'first_contact', label: t('flows.builder.trigger.firstContact') },
            { value: 'greeting', label: t('flows.builder.trigger.greeting') },
            { value: 'default', label: t('flows.builder.trigger.default') },
            { value: 'post_resolution', label: t('flows.builder.trigger.postResolution') },
            { value: 'message', label: t('flows.builder.field.anyMessage') },
            { value: 'always', label: t('flows.builder.field.always') },
            { value: 'webhook', label: t('flows.builder.field.webhook') }
          ]}
        />
        {kind === 'keyword' && (
          <>
            <TextInput
              label={t('flows.builder.field.keywords')}
              description={t('flows.builder.field.keywordsHint')}
              value={keywords.join(', ')}
              onChange={(e) => setKeywords(e.currentTarget.value)}
              placeholder={t('nav.menu')}
            />
            <Select
              label={t('flows.builder.field.keywordMode')}
              value={asStr(d.match) || 'contains'}
              onChange={(e) => onChange(patch(d, 'match', e.currentTarget.value === 'contains' ? undefined : e.currentTarget.value))}
              data={[
                { value: 'contains', label: t('flows.builder.field.keywordContains') },
                { value: 'exact', label: t('flows.builder.field.keywordExact') },
                { value: 'starts', label: t('flows.builder.field.keywordStarts') }
              ]}
            />
            <KeywordClashes keywords={keywords} flowId={attributes?.flowId} botId={attributes?.botId} hasIntent={intentEnabled(d)} t={t} />
          </>
        )}
        {kind === 'webhook' && <Text role="caption" c={MUTED}>{t('flows.builder.field.webhookHint')}</Text>}
        {intentAllowed(d) && <IntentFields d={d} onChange={onChange} t={t} botId={attributes?.botId} flowName={attributes?.flowName} />}
        <Text role="caption" c={MUTED}>{t('flows.builder.field.channelsHint')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Send: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.body')}
          description={varHint(variables, t)}
          value={asStr(d.body)}
          minRows={4}
          onChange={(e) => onChange(patch(d, 'body', e.currentTarget.value || undefined))}
        />
        <MediaFields d={d} onChange={onChange} t={t} />
        <OptionsEditor d={d} onChange={onChange} t={t} />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Ask: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const hasOptions = askOptions(d).length > 0;
    const fromList = listMenu(d);
    const validateKind = asStr(d.validate);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.question')}
          description={varHint(variables, t)}
          value={asStr(d.question) || asStr(d.body)}
          minRows={3}
          onChange={(e) => onChange(patch(patch(d, 'question', e.currentTarget.value || undefined), 'body', undefined))}
        />
        <TextInput
          label={t('flows.builder.field.variable')}
          description={t('flows.builder.field.variableHint')}
          value={asStr(d.variable)}
          placeholder="customer_name"
          onChange={(e) => onChange(patch(d, 'variable', e.currentTarget.value.replace(/[^A-Za-z0-9_]/g, '_') || undefined))}
        />
        {/* A question for a name, an email or a number answers itself when the channel or the contact book already
            knows it (8 October 2026); this asks anyway. */}
        <Switch
          label={t('flows.builder.field.alwaysAsk')}
          description={t('flows.builder.field.alwaysAskHint')}
          checked={d.always_ask === true}
          onChange={(checked) => onChange(patch(d, 'always_ask', checked || undefined))}
        />
        <ListMenuFields d={d} onChange={onChange} t={t} tokens={tokens} hint={hint} />
        <OptionsEditor d={d} onChange={onChange} t={t} />
        {!hasOptions && !fromList && <ValidationFields d={d} onChange={onChange} t={t} />}
        {/* Typed replies understood (19 October 2026): by meaning, a question answered, a date however it is written. */}
        {(hasOptions || fromList || validateKind === 'date' || validateKind === 'datetime') && (
          <Switch
            label={t('flows.builder.field.understand')}
            description={t('flows.builder.field.understandHint')}
            checked={d.understand_replies !== false}
            onChange={(checked) => onChange(patch(d, 'understand_replies', checked ? undefined : false))}
            data-testid="understand-replies"
          />
        )}
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.retryMessage')}
          description={retryHint(hasOptions ? 'option' : asStr(d.validate) || 'none', t)}
          value={asStr(d.retry_message)}
          onChange={(e) => onChange(patch(d, 'retry_message', e.currentTarget.value || undefined))}
        />
        <NumberInput
          label={t('flows.builder.field.maxRetries')}
          value={typeof d.max_retries === 'number' ? d.max_retries : 2}
          min={0}
          max={10}
          onChange={(v) => onChange(patch(d, 'max_retries', v === '' ? undefined : v))}
        />
        <MediaFields d={d} onChange={onChange} t={t} />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Several questions in a row, each checked, composed into one object for an API. */
  const Form: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const fields = formFields(d);
    const saveAs = asStr(d.save_as) || 'form';
    const write = (next: FormFieldSpec[]) => onChange(patch(d, 'fields', next.map((f) => ({
      name: f.name,
      question: f.question,
      type: f.type,
      required: f.required,
      ...(f.type === 'choice' ? { options: f.options } : {}),
      ...(f.label ? { label: f.label } : {}),
      ...(f.always_ask ? { always_ask: true } : {})
    }))));
    const update = (i: number, changes: Partial<FormFieldSpec>) => write(fields.map((f, j) => (j === i ? { ...f, ...changes } : f)));
    const move = (i: number, dir: -1 | 1) => {
      const next = [...fields];
      const [f] = next.splice(i, 1);
      next.splice(i + dir, 0, f);
      write(next);
    };
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.formIntro')}
          description={t('flows.builder.field.formIntroHint')}
          value={asStr(d.intro)}
          minRows={2}
          onChange={(e) => onChange(patch(d, 'intro', e.currentTarget.value || undefined))}
        />
        <Stack gap={8}>
          <Text role="inputLabel">{t('flows.builder.field.formFields')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.formFieldsHint')}</Text>
          {fields.length === 0 && <Text role="caption" c={MUTED}>{t('flows.builder.field.formNoFields')}</Text>}
          {fields.map((f, i) => (
            <div key={i} className="rr-inspector__field">
              <div className="rr-inspector__field-head">
                <Text role="bodySmall" fw={700}>{i + 1}. {f.label || f.name}</Text>
                <Group gap={2}>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.field.moveUp')} disabled={i === 0} onClick={() => move(i, -1)}>↑</ActionIcon>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.field.moveDown')} disabled={i === fields.length - 1} onClick={() => move(i, 1)}>↓</ActionIcon>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(fields.filter((_, j) => j !== i))}><CloseIcon /></ActionIcon>
                </Group>
              </div>
              <TextInput
                label={t('flows.builder.field.fieldQuestion')}
                value={f.question}
                onChange={(e) => {
                  const q = e.currentTarget.value;
                  const auto = !f.name || f.name === autoName(f.question) || /^field_\d+$/.test(f.name);
                  update(i, { question: q, ...(auto ? { name: autoName(q) || f.name } : {}) });
                }}
              />
              <div className="rr-inspector__row rr-inspector__row--pair">
                <TextInput
                  label={t('flows.builder.field.fieldSavedAs')}
                  value={f.name}
                  onChange={(e) => update(i, { name: e.currentTarget.value.replace(/[^A-Za-z0-9_]/g, '_') })}
                />
                <Select
                  label={t('flows.builder.field.fieldType')}
                  value={f.type}
                  onChange={(e) => update(i, { type: e.currentTarget.value as FieldType })}
                  data={FIELD_TYPES.map((v) => ({ value: v, label: t(`flows.builder.validate.${v}`) }))}
                />
              </div>
              <Text role="caption" c={MUTED}>{t(`flows.builder.validateDesc.${f.type}`)}</Text>
              {f.type === 'choice' && (
                <Textarea
                  label={t('flows.builder.field.fieldOptions')}
                  description={t('flows.builder.field.fieldOptionsHint')}
                  value={f.options.join('\n')}
                  minRows={2}
                  onChange={(e) => update(i, { options: e.currentTarget.value.split(/\r?\n/).map((x) => x.trim()).filter(Boolean) })}
                />
              )}
              <Switch
                label={t('flows.builder.field.fieldRequired')}
                description={t('flows.builder.field.fieldRequiredHint')}
                checked={f.required}
                onChange={(checked) => update(i, { required: checked })}
              />
            </div>
          ))}
          <Button size="xs" variant="subtle" disabled={fields.length >= 15} onClick={() => write([...fields, { name: `field_${fields.length + 1}`, question: '', type: 'text', required: true, options: [] }])}>
            + {t('flows.builder.field.addField')}
          </Button>
        </Stack>
        <Switch
          label={t('flows.builder.field.formConfirm')}
          description={t('flows.builder.field.formConfirmHint')}
          checked={d.confirm === true}
          onChange={(checked) => onChange(patch(d, 'confirm', checked))}
        />
        {/* A Skip button on optional typed questions (27 October 2026); on unless switched off. */}
        <Switch
          label={t('flows.builder.field.skipButton')}
          description={t('flows.builder.field.skipButtonHint')}
          checked={d.skip_button !== false}
          onChange={(checked) => onChange(patch(d, 'skip_button', checked))}
          data-testid="form-skip-button"
        />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.formDone')}
          description={t('flows.builder.field.formDoneHint')}
          value={asStr(d.done_message)}
          minRows={2}
          onChange={(e) => onChange(patch(d, 'done_message', e.currentTarget.value || undefined))}
        />
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.formCancel')}
          description={t('flows.builder.field.formCancelHint')}
          value={asStr(d.cancel_message)}
          onChange={(e) => onChange(patch(d, 'cancel_message', e.currentTarget.value || undefined))}
        />
        <TextInput
          label={t('flows.builder.field.saveAs')}
          description={t('flows.builder.field.saveAsFormHint')}
          value={asStr(d.save_as)}
          placeholder={'form' /* i18n-ignore: a variable name, not copy */}
          onChange={(e) => onChange(patch(d, 'save_as', e.currentTarget.value.replace(/[^A-Za-z0-9_]/g, '_') || undefined))}
        />
        {fields.length > 0 && (
          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.builder.field.formPreview')}</Text>
            <pre className="rr-inspector__preview">{`{{${saveAs}}} = ${JSON.stringify(formSample(fields), null, 2)}`}</pre>
          </Stack>
        )}
        <Text role="caption" c={MUTED}>{t('flows.builder.field.formPorts')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Llm: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const mode = llmMode(d);
    const kbText = Array.isArray(d.kb_ids) ? d.kb_ids.map(String).join('\n') : '';
    const selected = Array.isArray(d.kb_ids) ? d.kb_ids.map(String) : [];
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Select
          label={t('flows.builder.field.llmMode')}
          description={t(`flows.builder.llmModeHint.${mode}`)}
          value={mode}
          onChange={(e) => onChange(patch(d, 'mode', e.currentTarget.value === 'reply' ? undefined : e.currentTarget.value))}
          data={(['reply', 'classify', 'extract', 'rewrite'] as const).map((m) => ({ value: m, label: t(`flows.builder.llmMode.${m}`) }))}
        />
        <Textarea
          label={mode === 'rewrite' ? t('flows.builder.field.rewriteInstruction') : t('flows.builder.field.systemPrompt')}
          description={mode === 'rewrite' ? t('flows.builder.field.rewriteInstructionHint') : t('flows.builder.field.systemPromptHint')}
          value={asStr(d.system_prompt)}
          minRows={4}
          onChange={(e) => onChange(patch(d, 'system_prompt', e.currentTarget.value || undefined))}
        />
        {mode !== 'rewrite' && <Text role="caption" c={MUTED}>{t('flows.builder.field.llmKnowsHint')}</Text>}
        {mode === 'classify' && (
          <NamedItemsEditor
            d={d} onChange={onChange} field="labels" t={t}
            label={t('flows.builder.field.labels')} hint={t('flows.builder.field.labelsHint')}
            idLabel={t('flows.builder.field.labelId')} titleLabel={t('flows.builder.field.labelTitle')}
            addLabel={t('flows.builder.field.addLabel')} idPlaceholder={'billing' /* i18n-ignore: an example id */} titlePlaceholder={t('flows.builder.field.labelTitlePlaceholder')}
          />
        )}
        {mode === 'extract' && (
          <NamedItemsEditor
            d={d} onChange={onChange} field="fields" t={t}
            label={t('flows.builder.field.extractFields')} hint={t('flows.builder.field.extractFieldsHint')}
            idLabel={t('flows.builder.field.variable')} titleLabel={t('flows.builder.field.fieldDescription')}
            addLabel={t('flows.builder.field.addField')} idPlaceholder={'email' /* i18n-ignore: an example variable */} titlePlaceholder={t('flows.builder.field.fieldDescriptionPlaceholder')}
          />
        )}
        <PickOrType
          label={mode === 'rewrite' ? t('flows.builder.field.rewriteInput') : t('flows.builder.field.input')}
          description={mode === 'rewrite' ? t('flows.builder.field.rewriteInputHint') : varHint(variables, t)}
          value={asStr(d.input)}
          options={varOptions(variables, true)}
          tokens={tokens}
          fallbackHint={hint}
          placeholder={mode === 'rewrite' ? '{{http.body}}' : '{{message}}'}
          onChange={(v) => onChange(patch(d, 'input', v || undefined))}
          t={t}
        />
        {mode === 'rewrite' && (
          <Switch
            label={t('flows.builder.field.sendReply')}
            description={t('flows.builder.field.rewriteSendHint')}
            checked={d.send_reply !== false}
            onChange={(checked) => onChange(patch(d, 'send_reply', checked))}
          />
        )}
        {mode === 'reply' && (
          <>
            <Select
              label={t('flows.builder.field.kbScope')}
              description={t(`flows.builder.kbScopeHint.${kbScope(d)}`)}
              value={kbScope(d)}
              onChange={(e) => onChange(patch(d, 'kb_scope', e.currentTarget.value))}
              data={(['all', 'selected', 'none'] as const).map((v) => ({ value: v, label: t(`flows.builder.kbScope.${v}`) }))}
            />
            {kbScope(d) === 'selected' && (
              <>
                <KbPicker
                  selected={selected}
                  onChange={(ids) => onChange(patch(d, 'kb_ids', ids.length ? ids : undefined))}
                  fallbackText={kbText}
                />
                <KbUpload onIngested={(id) => onChange(patch(d, 'kb_ids', [...selected, id]))} />
              </>
            )}
            {kbScope(d) === 'all' && (
              <Text role="caption" c={MUTED}>{t('flows.builder.field.kbAllHint')} <Link to="/knowledge">{t('nav.knowledge')}</Link></Text>
            )}
            {kbScope(d) !== 'none' && (
              <Switch
                label={t('flows.builder.field.useLiveSources')}
                description={t('flows.builder.field.useLiveSourcesHint')}
                checked={d.use_live_sources !== false}
                onChange={(checked) => onChange(patch(d, 'use_live_sources', checked ? undefined : false))}
              />
            )}
            <Switch
              label={t('flows.builder.field.sendReply')}
              description={t('flows.builder.field.sendReplyHint')}
              checked={d.send_reply === true}
              onChange={(checked) => onChange(patch(d, 'send_reply', checked ? true : undefined))}
            />
            {/* Keep answering (19 October 2026): the step waits after its answer and answers follow-ups, typed or
                tapped, until the customer taps the button to move on. */}
            {d.send_reply === true && (
              <>
                <Switch
                  label={t('flows.builder.field.followUps')}
                  description={t('flows.builder.field.followUpsHint')}
                  checked={d.follow_ups === true}
                  onChange={(checked) => onChange(patch(d, 'follow_ups', checked ? true : undefined))}
                  data-testid="llm-follow-ups"
                />
                {d.follow_ups === true && (
                  <TextInput
                    label={t('flows.builder.field.moveOnLabel')}
                    description={t('flows.builder.field.moveOnLabelHint')}
                    value={asStr(d.move_on_label)}
                    placeholder={t('flows.builder.field.moveOnLabelPlaceholder')}
                    maxLength={20}
                    onChange={(e) => onChange(patch(d, 'move_on_label', e.currentTarget.value || undefined))}
                    data-testid="llm-move-on-label"
                  />
                )}
              </>
            )}
            <Select
              label={t('flows.builder.field.unsureBelow')}
              description={t('flows.builder.field.unsureBelowHint')}
              value={String(typeof d.unsure_below === 'number' ? d.unsure_below : 0.6)}
              onChange={(e) => onChange(patch(d, 'unsure_below', Number(e.currentTarget.value)))}
              data={['0.4', '0.5', '0.6', '0.7', '0.8'].map((v) => ({ value: v, label: t('flows.builder.field.unsurePercent', { value: Math.round(Number(v) * 100) }) }))}
            />
          </>
        )}
        <TextInput
          label={t('flows.builder.field.saveAs')}
          description={t(mode === 'classify' ? 'flows.builder.field.saveAsClassifyHint' : mode === 'extract' ? 'flows.builder.field.saveAsExtractHint' : mode === 'rewrite' ? 'flows.builder.field.saveAsRewriteHint' : 'flows.builder.field.saveAsLlmHint')}
          value={asStr(d.save_as)}
          placeholder={'llm' /* i18n-ignore: a variable name, not copy */}
          onChange={(e) => onChange(patch(d, 'save_as', e.currentTarget.value || undefined))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Approved templates, by name; the body's {{n}} placeholders become the variable rows. */
  const Template: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const list = useQuery({ queryKey: ['templates'], queryFn: () => templates.list(), staleTime: 60_000, retry: false });
    const approved = (list.data?.data ?? []).filter((x) => x.status === 'approved');
    const chosen = approved.find((x) => x.code === asStr(d.template_name));
    const placeholders = countPlaceholders(chosen?.components);
    const values = Array.isArray(d.variables) ? d.variables.map((v) => asStr(isPlainObject(v) ? v.value : v)) : [];
    const rows = Array.from({ length: Math.max(placeholders, values.length) }, (_, i) => values[i] ?? '');
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Select
          label={t('flows.builder.field.template')}
          description={t('flows.builder.field.templateHint')}
          value={asStr(d.template_name)}
          onChange={(e) => {
            const tpl = approved.find((x) => x.code === e.currentTarget.value);
            onChange(patch(patch(patch(d, 'template_name', e.currentTarget.value || undefined), 'language', tpl?.language ?? d.language), 'category', tpl?.category ?? undefined));
          }}
          data={[{ value: '', label: t('flows.builder.field.templateChoose') }, ...approved.map((x) => ({ value: x.code, label: `${x.code} (${x.language})` }))]}
        />
        {!list.isPending && approved.length === 0 && (
          <Text role="caption" c={MUTED}>{t('flows.builder.field.templateNone')} <Link to="/templates">{t('nav.templates')}</Link></Text>
        )}
        {rows.length > 0 && (
          <Stack gap={6}>
            <Text role="inputLabel">{t('flows.builder.field.templateVariables')}</Text>
            <Text role="caption" c={MUTED}>{varHint(variables, t)}</Text>
            {rows.map((v, i) => (
              <PickOrType
                key={i}
                ariaLabel={`{{${i + 1}}}`}
                placeholder={`{{${i + 1}}}`}
                value={v}
                options={varOptions(variables, true)}
                tokens={tokens}
                fallbackHint={hint}
                onChange={(value) => {
                  const next = [...rows];
                  next[i] = value;
                  onChange(patch(d, 'variables', next.map((x) => ({ value: x }))));
                }}
                t={t}
              />
            ))}
          </Stack>
        )}
        {placeholders === 0 && (
          <Button size="xs" variant="subtle" onClick={() => onChange(patch(d, 'variables', [...rows, ''].map((x) => ({ value: x }))))}>
            + {t('flows.builder.field.addVariable')}
          </Button>
        )}
        {hasMediaHeader(chosen?.components) && <MediaFields d={d} onChange={onChange} t={t} />}
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** One branch per case: a variable compared with each value; Else for the rest. */
  const SwitchNode: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const raw = asRows(d.cases).map((r) => ({ value: asStr(r.value) }));
    const rows = raw.length ? raw : switchCases(d).map((c) => ({ value: c.value }));
    const write = (next: Array<{ value: string }>) => onChange(patch(d, 'cases', next.length ? next : undefined));
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <PickOrType
          label={t('flows.builder.field.switchVariable')}
          description={varHint(variables, t)}
          value={asStr(d.variable)}
          options={varOptions(variables)}
          placeholder={'need' /* i18n-ignore: an example variable */}
          onChange={(v) => onChange(patch(d, 'variable', v || undefined))}
          t={t}
        />
        <Stack gap={6}>
          <Text role="inputLabel">{t('flows.builder.field.cases')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.casesHint')}</Text>
          {rows.map((row, i) => (
            <div key={i} className="rr-inspector__row rr-inspector__row--single">
              <TextInput
                aria-label={t('flows.builder.field.caseValue')}
                placeholder={t('flows.builder.field.caseValue')}
                value={row.value}
                onChange={(e) => write(rows.map((r, j) => (j === i ? { value: e.currentTarget.value } : r)))}
              />
              <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(rows.filter((_, j) => j !== i))}>
                <CloseIcon />
              </ActionIcon>
            </div>
          ))}
          <Button size="xs" variant="subtle" onClick={() => write([...rows, { value: '' }])}>+ {t('flows.builder.field.addCase')}</Button>
        </Stack>
        <datalist id="rr-flow-variables">{variables.map((v) => <option key={v} value={v} />)}</datalist>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * The business's opening hours (Settings, Organisation), or days, opening
   * and closing time set on this step, in a time zone.
   */
  const Hours: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const source = hoursSource(d);
    const days = (Array.isArray(d.days) ? d.days : [1, 2, 3, 4, 5]).map(Number).filter((n) => n >= 1 && n <= 7);
    const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const choose = (next: HoursSource) => {
      if (next === 'business') return onChange(patch(d, 'source', 'business'));
      // The step's own hours start from what the engine assumed, written down so they show and save.
      onChange({ ...d, source: 'custom', days: Array.isArray(d.days) ? d.days : [1, 2, 3, 4, 5], open: asStr(d.open) || '08:00', close: asStr(d.close) || '17:00' });
    };
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <RadioGroup label={t('flows.builder.field.hoursSource')} value={source} onChange={(v) => choose(v as HoursSource)}>
          <Radio value="business" label={t('flows.builder.hoursSource.business')} description={t('flows.builder.field.hoursBusinessHint')} />
          <Radio value="custom" label={t('flows.builder.hoursSource.custom')} />
        </RadioGroup>
        {source === 'custom' && (
          <>
            <Stack gap={4}>
              <Text role="inputLabel">{t('flows.builder.field.openDays')}</Text>
              <div className="rr-inspector__days">
                {dayKeys.map((k, i) => {
                  const n = i + 1;
                  const on = days.includes(n);
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`rr-inspector__day${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => onChange(patch(d, 'days', on ? days.filter((x) => x !== n) : [...days, n].sort((a, b) => a - b)))}
                    >
                      {t(`flows.builder.day.${k}`)}
                    </button>
                  );
                })}
              </div>
            </Stack>
            <div className="rr-inspector__row rr-inspector__row--pair">
              <TextInput label={t('flows.builder.field.opens')} type="time" value={asStr(d.open) || '08:00'} onChange={(e) => onChange(patch(d, 'open', e.currentTarget.value))} />
              <TextInput label={t('flows.builder.field.closes')} type="time" value={asStr(d.close) || '17:00'} onChange={(e) => onChange(patch(d, 'close', e.currentTarget.value))} />
            </div>
            <TextInput
              label={t('flows.builder.field.timezone')}
              description={t('flows.builder.field.hoursTimezoneHint')}
              value={asStr(d.timezone)}
              list="rr-flow-timezones"
              onChange={(e) => onChange(patch(d, 'timezone', e.currentTarget.value || undefined))}
            />
            <datalist id="rr-flow-timezones">
              {['Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Cairo', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'Asia/Dubai', 'UTC'].map((z) => <option key={z} value={z} />)}
            </datalist>
          </>
        )}
        <Text role="caption" c={MUTED}>{t('flows.builder.field.hoursPorts')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Branches with weights: an A/B test. */
  const Split: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const branches = splitBranches(d);
    const total = branches.reduce((a, b) => a + b.weight, 0) || 1;
    const write = (next: typeof branches) => onChange(patch(d, 'branches', next.map((b, i) => ({ id: b.id || slug(b.title, i + 1), title: b.title, weight: b.weight }))));
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="caption" c={MUTED}>{t('flows.builder.field.splitHint')}</Text>
        {branches.map((b, i) => (
          <div key={i} className="rr-inspector__row rr-inspector__row--option">
            <TextInput
              aria-label={t('flows.builder.field.branchName')}
              placeholder={t('flows.builder.field.branchName')}
              value={b.title}
              onChange={(e) => write(branches.map((x, j) => (j === i ? { ...x, title: e.currentTarget.value } : x)))}
            />
            <NumberInput
              aria-label={t('flows.builder.field.weight')}
              value={b.weight}
              min={0}
              max={100}
              onChange={(v) => write(branches.map((x, j) => (j === i ? { ...x, weight: v === '' ? 0 : Number(v) } : x)))}
            />
            <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} disabled={branches.length <= 2} onClick={() => write(branches.filter((_, j) => j !== i))}>
              <CloseIcon />
            </ActionIcon>
          </div>
        ))}
        <Text role="caption" c={MUTED}>{branches.map((b) => `${b.title} ${Math.round((b.weight / total) * 100)}%`).join(' · ')}</Text>
        <Button size="xs" variant="subtle" disabled={branches.length >= 5} onClick={() => write([...branches, { id: '', title: String.fromCharCode(65 + branches.length), weight: 1 }])}>
          + {t('flows.builder.field.addBranch')}
        </Button>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Call an API: how to reach it, how to prove who you are, what to send, what to keep, what to tell the customer. */
  const Http: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    // The saved flow this step belongs to: "Test this request" borrows its saved sign-in secrets.
    const { id: flowId } = useParams();
    const headers = isPlainObject(d.headers) ? Object.entries(d.headers).map(([k, v]) => ({ k, v: asStr(v) })) : [];
    const extract = asRows(d.extract).map((e) => ({ k: asStr(e.name), v: asStr(e.path) }));
    const writeExtract = (rows: Array<{ k: string; v: string }>) => onChange(patch(d, 'extract', rows.length ? rows.map((r) => ({ name: r.k, path: r.v })) : undefined));
    const method = (asStr(d.method) || 'GET').toUpperCase();

    const auth: JsonObject = isPlainObject(d.auth) ? d.auth : {};

    const bodyText = typeof d.body === 'string' ? d.body : '';
    const bodyForm = forms.find((f) => `{{${f.saveAs}}}` === bodyText.replace(/\s+/g, ''));
    const bodySource = bodyForm ? 'form' : asStr(d.content_type) === 'text' ? 'text' : asStr(d.content_type) === 'form' ? 'urlencoded' : 'json';

    const reply: JsonObject = isPlainObject(d.reply) ? d.reply : {};
    const replyPaths = Array.isArray(reply.paths) ? reply.paths.map((p) => (isPlainObject(p) ? { path: asStr(p.path), label: asStr(p.label) } : { path: asStr(p), label: '' })) : [];
    const setReply = (changes: Record<string, unknown>) => {
      const next: Record<string, unknown> = { ...reply, ...changes };
      for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
      onChange(patch(d, 'reply', next));
    };
    const writeReplyPaths = (rows: Array<{ path: string; label: string }>) => setReply({ send: true, paths: rows.map((r) => (r.label ? { path: r.path, label: r.label } : { path: r.path })) });

    // "Test this request": sample values for the variables it uses, then the response and its paths.
    const used = useMemo(() => usedVariables(d), [d]);
    const [sample, setSample] = useState<Record<string, string>>({});
    const [probe, setProbe] = useState<flows.HttpProbeResult | null>(null);
    const [probing, setProbing] = useState(false);
    const [probeError, setProbeError] = useState<string | null>(null);
    const pathOptions = (probe?.paths ?? []).map((p) => ({ value: p.path, label: `${p.path}  ·  ${p.sample}` }));
    // The lists and objects of the response, for "which part should the AI put into words": `items` from `items[0].name`.
    const partOptions = [...new Set((probe?.paths ?? []).flatMap((p) => {
      const list = p.path.match(/^(.*?)(\[0\]|\.length$)/)?.[1];
      const parent = p.path.includes('.') ? p.path.slice(0, p.path.lastIndexOf('.')).replace(/\[0\]$/, '') : '';
      return [list, parent].filter((x): x is string => !!x && x !== 'length');
    }))].sort().map((path) => ({ value: path, label: path }));
    const replyByAi = reply.mode === 'ai';
    async function runProbe() {
      setProbing(true);
      setProbeError(null);
      try {
        const res = await flows.probeHttp(d, sampleObject(sample), flowId ? { flowId, nodeId: node.id } : undefined);
        setProbe(res.data);
      } catch (e) {
        setProbeError(e instanceof Error ? e.message : t('common.unknownError'));
      } finally {
        setProbing(false);
      }
    }

    // A saved API connection (19 October 2026): the address and sign-in come from it, and its actions fill the step.
    const apis = useApiSetup();
    const connectionId = asStr(d.connection_id);
    const connection = apis.connections.find((c) => c.id === connectionId) ?? null;
    const pickConnection = (id: string) => {
      const chosen = apis.connections.find((c) => c.id === id);
      let next = patch(patch(d, 'connection_id', id || undefined), 'connection_name', chosen?.name);
      if (id) next = patch(next, 'auth', undefined);
      onChange(next);
    };
    const pickAction = (id: string) => {
      const a = connection?.actions.find((x) => x.id === id);
      if (!a) return;
      let next = patch(patch(d, 'method', a.method), 'url', a.path);
      next = patch(next, 'body', a.body == null ? undefined : a.body);
      if (!asStr(d.name).trim()) next = patch(next, 'name', a.name);
      onChange(next);
    };

    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        {(apis.connections.length > 0 || connectionId) && (
          <Stack gap={6} data-testid="http-connection">
            <Select
              label={t('flows.builder.http.connection')}
              description={connectionId ? t('flows.builder.http.connectionOnHint', { base: connection?.base_url ?? '' }) : t('flows.builder.http.connectionHint')}
              value={connectionId}
              onChange={(e) => pickConnection(e.currentTarget.value)}
              data={[
                { value: '', label: t('flows.builder.http.connectionOwn') },
                ...apis.connections.map((c) => ({ value: c.id, label: c.name })),
                ...(connectionId && !connection && !apis.loading ? [{ value: connectionId, label: t('flows.builder.http.connectionGone') }] : [])
              ]}
            />
            {connection && connection.actions.length > 0 && (
              <Select
                label={t('flows.builder.http.action')}
                description={t('flows.builder.http.actionHint')}
                value=""
                onChange={(e) => pickAction(e.currentTarget.value)}
                data={[{ value: '', label: t('flows.builder.http.actionPick') }, ...connection.actions.map((a) => ({ value: a.id, label: `${a.name} (${a.method} ${a.path})` }))]}
              />
            )}
          </Stack>
        )}
        <div className="rr-inspector__row rr-inspector__row--method">
          <Select
            label={t('flows.builder.field.method')}
            value={method}
            onChange={(e) => onChange(patch(d, 'method', e.currentTarget.value))}
            data={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({ value: m, label: m }))}
          />
          <TokenInput tokens={tokens} fallbackHint={hint}
            label={t(connectionId ? 'flows.builder.http.path' : 'flows.builder.field.url')}
            description={varHint(variables, t)}
            value={asStr(d.url)}
            placeholder={connectionId ? '/orders/{{order_id}}' : 'https://api.example.com/orders/{{order_id}}'}
            onChange={(e) => onChange(patch(d, 'url', e.currentTarget.value || undefined))}
          />
        </div>

        {/* Secrets are stored encrypted and never sent back: the step only knows which ones are saved. */}
        {connectionId ? (
          <Text role="caption" c={MUTED}>{t('flows.builder.http.signsIn', { name: connection?.name ?? asStr(d.connection_name) })}</Text>
        ) : (
          <HttpAuthFields
            auth={auth}
            onChange={(next) => onChange(patch(d, 'auth', next))}
            t={t}
            secretHint={t('flows.builder.field.authSecretHint')}
            saved={Array.isArray(auth.saved) ? auth.saved.map(String) : []}
            savedPlaceholder={t('flows.builder.field.authSecretSaved')}
          />
        )}

        <CacheFields d={d} onChange={onChange} t={t} method={method} />

        <Stack gap={4}>
          <Text role="inputLabel">{t('flows.builder.field.headers')}</Text>
          <PairRows
            rows={headers}
            onChange={(rows) => onChange(patch(d, 'headers', rows.length ? Object.fromEntries(rows.filter((r) => r.k.trim()).map((r) => [r.k.trim(), r.v])) : undefined))}
            keyLabel={t('flows.builder.field.headerName')}
            valueLabel={t('flows.builder.field.headerValue')}
            addLabel={t('flows.builder.field.addHeader')}
            keyPlaceholder={'Accept' /* i18n-ignore: an HTTP header name */}
            valuePlaceholder={'application/json' /* i18n-ignore: an example header value */}
            keyList="rr-http-headers"
            t={t}
          />
          <datalist id="rr-http-headers">{COMMON_HEADERS.map((h) => <option key={h} value={h} />)}</datalist>
        </Stack>

        {method !== 'GET' && (
          <>
            <Select
              label={t('flows.builder.field.bodySource')}
              value={bodySource}
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (v === 'form') onChange(patch(patch(d, 'body', forms[0] ? `{{${forms[0].saveAs}}}` : ''), 'content_type', undefined));
                else if (v === 'text') onChange(patch(patch(d, 'content_type', 'text'), 'body', bodyForm ? undefined : d.body));
                else if (v === 'urlencoded') onChange(patch(patch(d, 'content_type', 'form'), 'body', bodyForm ? undefined : d.body));
                else onChange(patch(patch(d, 'content_type', undefined), 'body', bodyForm ? undefined : d.body));
              }}
              data={[
                { value: 'json', label: t('flows.builder.bodySource.json') },
                { value: 'form', label: t('flows.builder.bodySource.form') },
                { value: 'text', label: t('flows.builder.bodySource.text') },
                { value: 'urlencoded', label: t('flows.builder.field.form') }
              ]}
            />
            {bodySource === 'form' && (forms.length === 0
              ? <Text role="caption" c={MUTED}>{t('flows.builder.field.bodyFormNone')}</Text>
              : (
                <Select
                  label={t('flows.builder.field.bodyForm')}
                  description={t('flows.builder.field.bodyFormHint')}
                  value={bodyForm?.id ?? ''}
                  onChange={(e) => { const f = forms.find((x) => x.id === e.currentTarget.value); if (f) onChange(patch(d, 'body', `{{${f.saveAs}}}`)); }}
                  data={[{ value: '', label: t('flows.builder.field.bodyFormChoose') }, ...forms.map((f) => ({ value: f.id, label: `${f.name} ({{${f.saveAs}}})` }))]}
                />
              ))}
            {bodySource !== 'form' && (
              <Textarea
                label={t('flows.builder.field.requestBody')}
                description={t('flows.builder.field.requestBodyHint')}
                value={typeof d.body === 'string' ? d.body : d.body == null ? '' : JSON.stringify(d.body, null, 2)}
                minRows={4}
                className="rr-flow-builder__json"
                onChange={(e) => {
                  const raw = e.currentTarget.value;
                  let value: unknown = raw;
                  const trimmed = raw.trim();
                  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                    try { value = JSON.parse(raw); } catch { value = raw; }
                  }
                  onChange(patch(d, 'body', raw ? value : undefined));
                }}
              />
            )}
          </>
        )}

        <TextInput
          label={t('flows.builder.field.saveAs')}
          description={t('flows.builder.field.saveAsHttpHint')}
          value={asStr(d.save_as)}
          placeholder={'http' /* i18n-ignore: a variable name, not copy */}
          onChange={(e) => onChange(patch(d, 'save_as', e.currentTarget.value || undefined))}
        />

        <Stack gap={4}>
          <Text role="inputLabel">{t('flows.builder.field.extract')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.extractHint')}</Text>
          {extract.map((row, i) => (
            <div key={i} className="rr-inspector__row">
              <TextInput
                aria-label={t('flows.builder.field.variable')}
                placeholder={'order_status' /* i18n-ignore: an example variable */}
                value={row.k}
                onChange={(e) => writeExtract(extract.map((r, j) => (j === i ? { ...r, k: e.currentTarget.value.replace(/[^A-Za-z0-9_.]/g, '_') } : r)))}
              />
              <PickOrType
                ariaLabel={t('flows.builder.field.path')}
                placeholder={'data.items[0].status' /* i18n-ignore: an example path */}
                value={row.v}
                options={pathOptions}
                onChange={(v) => writeExtract(extract.map((r, j) => (j === i ? { ...r, v } : r)))}
                t={t}
              />
              <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => writeExtract(extract.filter((_, j) => j !== i))}>
                <CloseIcon />
              </ActionIcon>
            </div>
          ))}
          <Button size="xs" variant="subtle" onClick={() => writeExtract([...extract, { k: '', v: '' }])}>+ {t('flows.builder.field.addExtract')}</Button>
        </Stack>

        <Stack gap={6}>
          <Text role="inputLabel">{t('flows.builder.field.replySection')}</Text>
          <Switch
            label={t('flows.builder.field.replySend')}
            description={t('flows.builder.field.replySendHint')}
            checked={reply.send === true}
            onChange={(checked) => (checked ? setReply({ send: true }) : onChange(patch(d, 'reply', undefined)))}
          />
          {reply.send === true && (
            <Switch
              label={t('flows.builder.field.httpReplyAi')}
              description={t('flows.builder.field.httpReplyAiHint')}
              checked={replyByAi}
              onChange={(checked) => setReply({ mode: checked ? 'ai' : undefined })}
            />
          )}
          {reply.send === true && replyByAi && (
            <>
              <Textarea
                label={t('flows.builder.field.rewriteInstruction')}
                description={t('flows.builder.field.rewriteInstructionHint')}
                value={asStr(reply.instruction)}
                minRows={3}
                onChange={(e) => setReply({ instruction: e.currentTarget.value })}
              />
              <PickOrType
                label={t('flows.builder.field.httpReplyAiPath')}
                description={t('flows.builder.field.httpReplyAiPathHint')}
                value={asStr(reply.path)}
                options={partOptions}
                placeholder={'data.locations' /* i18n-ignore: an example path */}
                onChange={(v) => setReply({ path: v })}
                t={t}
              />
              <TextInput
                label={t('flows.builder.field.replyText')}
                description={t('flows.builder.field.httpReplyAiLeadHint')}
                value={asStr(reply.text)}
                onChange={(e) => setReply({ text: e.currentTarget.value })}
              />
              <TextInput
                label={t('flows.builder.field.replyFallback')}
                description={t('flows.builder.field.httpReplyAiFallbackHint')}
                value={asStr(reply.fallback)}
                onChange={(e) => setReply({ fallback: e.currentTarget.value })}
              />
              <Text role="caption" c={MUTED}>{t('flows.builder.field.httpReplyAiLimit')}</Text>
            </>
          )}
          {reply.send === true && !replyByAi && (
            <>
              <TextInput
                label={t('flows.builder.field.replyText')}
                description={t('flows.builder.field.replyTextHint')}
                value={asStr(reply.text)}
                onChange={(e) => setReply({ text: e.currentTarget.value })}
              />
              <Text role="inputLabel">{t('flows.builder.field.replyPaths')}</Text>
              <Text role="caption" c={MUTED}>{t('flows.builder.field.replyPathsHint')}</Text>
              {replyPaths.map((row, i) => (
                <div key={i} className="rr-inspector__row">
                  <PickOrType
                    ariaLabel={t('flows.builder.field.path')}
                    placeholder={'message.text' /* i18n-ignore: an example path */}
                    value={row.path}
                    options={pathOptions}
                    onChange={(v) => writeReplyPaths(replyPaths.map((r, j) => (j === i ? { ...r, path: v } : r)))}
                    t={t}
                  />
                  <TextInput
                    aria-label={t('flows.builder.field.replyLabel')}
                    placeholder={t('flows.builder.field.replyLabel')}
                    value={row.label}
                    onChange={(e) => writeReplyPaths(replyPaths.map((r, j) => (j === i ? { ...r, label: e.currentTarget.value } : r)))}
                  />
                  <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => writeReplyPaths(replyPaths.filter((_, j) => j !== i))}>
                    <CloseIcon />
                  </ActionIcon>
                </div>
              ))}
              <Button size="xs" variant="subtle" onClick={() => writeReplyPaths([...replyPaths, { path: '', label: '' }])}>+ {t('flows.builder.field.addReplyPath')}</Button>
              <TextInput
                label={t('flows.builder.field.replyFallback')}
                description={t('flows.builder.field.replyFallbackHint')}
                value={asStr(reply.fallback)}
                onChange={(e) => setReply({ fallback: e.currentTarget.value })}
              />
            </>
          )}
        </Stack>

        <div className="rr-inspector__probe">
          <Text role="inputLabel">{t('flows.builder.field.probe')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.probeHint')}</Text>
          {used.length > 0 && (
            <Stack gap={4}>
              <Text role="caption" c={MUTED}>{t('flows.builder.field.probeSampleHint')}</Text>
              {used.map((v) => (
                <TextInput key={v} label={`{{${v}}}`} value={sample[v] ?? ''} onChange={(e) => setSample({ ...sample, [v]: e.currentTarget.value })} />
              ))}
            </Stack>
          )}
          <div>
            <Button size="xs" variant="default" loading={probing} disabled={!asStr(d.url).trim()} onClick={runProbe}>{t('flows.builder.field.probeRun')}</Button>
          </div>
          {probeError && <Text role="caption" c="var(--rr-color-error, #dc2626)">{probeError}</Text>}
          {probe && (
            <Stack gap={6}>
              <Text role="bodySmall" fw={600} c={probe.ok ? 'var(--rr-color-success, #16a34a)' : 'var(--rr-color-error, #dc2626)'}>
                {probe.ok ? t('flows.builder.field.probeStatus', { status: probe.status }) : t('flows.builder.field.probeFailed', { error: probe.error ?? probe.detail })}
              </Text>
              {probe.paths.length > 0 && (
                <div className="rr-inspector__paths">
                  <Text role="caption" c={MUTED}>{t('flows.builder.field.probePaths')}</Text>
                  {probe.paths.slice(0, 80).map((p) => (
                    <div key={p.path} className="rr-inspector__path">
                      <code>{p.path}</code>
                      <span className="rr-inspector__path-sample" title={p.sample}>{p.sample}</span>
                      <button type="button" className="rr-inspector__path-act" onClick={() => writeExtract([...extract.filter((r) => r.k || r.v), { k: lastSegment(p.path), v: p.path }])}>{t('flows.builder.field.probeExtract')}</button>
                      <button type="button" className="rr-inspector__path-act" onClick={() => writeReplyPaths([...replyPaths.filter((r) => r.path), { path: p.path, label: '' }])}>{t('flows.builder.field.probeShow')}</button>
                    </div>
                  ))}
                </div>
              )}
              <details className="rr-inspector__advanced">
                <summary>{t('flows.builder.field.probeBody')}</summary>
                <pre className="rr-inspector__preview">{JSON.stringify(probe.body, null, 2)}</pre>
              </details>
              {Object.keys(probe.extracted ?? {}).length > 0 && (
                <Text role="caption" c={MUTED}>{t('flows.builder.field.probeExtracted')}: {Object.entries(probe.extracted).map(([k, v]) => `${k} = ${JSON.stringify(v)}`).join(' · ')}</Text>
              )}
              <Stack gap={2}>
                <Text role="caption" c={MUTED}>{t('flows.builder.field.probeReply')}</Text>
                {probe.reply ? <div className="rr-inspector__bubble">{probe.reply}</div>
                  : <Text role="caption" c={MUTED}>{probe.reply_by_ai ? t('flows.builder.field.httpReplyAiProbe') : t('flows.builder.field.probeReplyNone')}</Text>}
              </Stack>
            </Stack>
          )}
        </div>

        <NumberInput
          label={t('flows.builder.field.timeout')}
          value={typeof d.timeout_ms === 'number' ? d.timeout_ms : ''}
          min={1000}
          max={60000}
          step={1000}
          onChange={(v) => onChange(patch(d, 'timeout_ms', v === '' ? undefined : v))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Contact: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    // The languages a contact can be set to (1 November 2026): the list a chatbot speaks from, the same one the
    // contact form offers, each named in its own language.
    const { languages } = useLanguages();
    const { locale: consoleLocale } = useI18n();
    const fields = isPlainObject(d.fields) ? Object.entries(d.fields).map(([k, v]) => ({ k, v: asStr(v) })) : [];
    const tags = (key: string) => (Array.isArray(d[key]) ? (d[key] as unknown[]).map(String).join(', ') : '');
    const setTags = (key: string, raw: string) => onChange(patch(d, key, raw.split(',').map((x) => x.trim()).filter(Boolean).length ? raw.split(',').map((x) => x.trim()).filter(Boolean) : undefined));
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="caption" c={MUTED}>{varHint(variables, t)}</Text>
        <PickOrType
          label={t('flows.builder.field.contactName')}
          value={asStr(d.contact_name ?? d.set_name)}
          options={varOptions(variables, true)}
          tokens={tokens}
          fallbackHint={hint}
          placeholder="{{customer_name}}"
          onChange={(v) => onChange(patch(patch(d, 'contact_name', v || undefined), 'set_name', undefined))}
          t={t}
        />
        <PickOrType
          label={t('flows.builder.field.contactEmail')}
          value={asStr(d.email)}
          options={varOptions(variables, true)}
          tokens={tokens}
          fallbackHint={hint}
          placeholder="{{email}}"
          onChange={(v) => onChange(patch(d, 'email', v || undefined))}
          t={t}
        />
        {/* The contact's language (4 October 2026): the engine always read
            `locale`, and nothing in the builder could set it. */}
        <PickOrType
          // Mounted again once the list arrives, so a saved "zu" opens on the list rather than in the typing box.
          key={languages.length > 0 ? 'languages' : 'loading'}
          label={t('flows.builder.field.contactLocale')}
          description={t('flows.builder.field.contactLocaleHint')}
          value={asStr(d.locale)}
          options={[...languageOptions(languages, consoleLocale), ...varOptions(variables, true)]}
          tokens={tokens}
          fallbackHint={hint}
          placeholder="{{language}}"
          onChange={(v) => onChange(patch(d, 'locale', v || undefined))}
          t={t}
        />
        {/* Never silently (owner's decision, 4 October 2026, after a flow
            replaced "Stanley" with "Stan"). On unless switched off: an absent
            value reads as on, here and in the engine. */}
        <Switch
          label={t('flows.builder.field.confirmChanges')}
          description={t('flows.builder.field.confirmChangesHint')}
          checked={d.confirm_changes !== false}
          onChange={(checked) => onChange(patch(d, 'confirm_changes', checked))}
        />
        {d.confirm_changes !== false && (
          <TokenTextarea tokens={tokens} fallbackHint={hint}
            label={t('flows.builder.field.confirmChangeMessage')}
            description={t('flows.builder.field.confirmChangeMessageHint')}
            placeholder={t('flows.builder.field.confirmChangeMessagePlaceholder')}
            value={asStr(d.confirm_change_message)}
            minRows={2}
            onChange={(e) => onChange(patch(d, 'confirm_change_message', e.currentTarget.value || undefined))}
          />
        )}
        <Stack gap={4}>
          <Text role="inputLabel">{t('flows.builder.field.customFields')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.customFieldsHint')}</Text>
          <PairRows
            rows={fields}
            onChange={(rows) => onChange(patch(d, 'fields', rows.length ? Object.fromEntries(rows.filter((r) => r.k.trim()).map((r) => [r.k.trim(), r.v])) : undefined))}
            keyLabel={t('flows.builder.field.fieldName')}
            valueLabel={t('flows.builder.field.fieldValue')}
            addLabel={t('flows.builder.field.addField')}
            keyPlaceholder="order_number"
            valuePlaceholder="{{order_number}}"
            keyList={fieldKeys.length > 0 ? CONTACT_FIELD_KEYS_LIST : undefined}
            valueTokens={tokens}
            fallbackHint={hint}
            t={t}
          />
          {/* The keys already defined under Settings, so a builder picks
              "city" rather than adding "City" beside it. A plain key, no
              braces: this is the field being written, not a template. */}
          {fieldKeys.length > 0 && (
            <datalist id={CONTACT_FIELD_KEYS_LIST}>{fieldKeys.map((k) => <option key={k} value={k} />)}</datalist>
          )}
        </Stack>
        <TextInput
          label={t('flows.builder.field.addTags')}
          description={t('flows.builder.field.tagsHint')}
          value={tags('add_tags')}
          onChange={(e) => setTags('add_tags', e.currentTarget.value)}
        />
        <TextInput
          label={t('flows.builder.field.removeTags')}
          value={tags('remove_tags')}
          onChange={(e) => setTags('remove_tags', e.currentTarget.value)}
        />
        <Switch
          label={t('flows.builder.field.optOut')}
          description={t('flows.builder.field.optOutHint')}
          checked={d.opt_out === true}
          onChange={(checked) => onChange(patch(d, 'opt_out', checked ? true : undefined))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Handoff: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const always = d.always === true;
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Switch
          label={t('flows.builder.field.handoffAlways')}
          description={t('flows.builder.field.handoffAlwaysHint')}
          checked={always}
          onChange={(checked) => onChange(patch(d, 'always', checked ? true : undefined))}
        />
        {!always && (
          <NumberInput
            label={t('flows.builder.field.threshold')}
            description={t('flows.builder.field.thresholdHint')}
            value={typeof d.threshold === 'number' ? d.threshold : ''}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => onChange(patch(d, 'threshold', v === '' ? undefined : v))}
          />
        )}
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.handoffMessage')}
          description={t('flows.builder.field.handoffMessageHint')}
          value={asStr(d.message)}
          minRows={2}
          onChange={(e) => onChange(patch(d, 'message', e.currentTarget.value || undefined))}
        />
        <TextInput
          label={t('flows.builder.field.reason')}
          value={asStr(d.reason)}
          onChange={(e) => onChange(patch(d, 'reason', e.currentTarget.value || undefined))}
          placeholder="customer_request"
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const SetVar: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const rows = asRows(d.assignments).map((a) => ({ k: asStr(a.name), v: asStr(a.value) }));
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Stack gap={4}>
          <Text role="inputLabel">{t('flows.builder.field.assignments')}</Text>
          <Text role="caption" c={MUTED}>{varHint(variables, t)}</Text>
          <PairRows
            rows={rows}
            onChange={(next) => onChange(patch(d, 'assignments', next.map((r) => ({ name: r.k, value: r.v }))))}
            keyLabel={t('flows.builder.field.variable')}
            valueLabel={t('flows.builder.field.value')}
            addLabel={t('flows.builder.field.addAssignment')}
            keyPlaceholder="total"
            valuePlaceholder="{{qty}}"
            valueTokens={tokens}
            fallbackHint={hint}
            t={t}
          />
        </Stack>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Condition: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <RulesEditor d={d} onChange={onChange} variables={variables} t={t} />
        <Text role="caption" c={MUTED}>{t('flows.builder.field.conditionPorts')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * The Decision step (1 October 2026): named paths, each a set of rules on
   * data, checked top to bottom. A path's exit id is given once, when the
   * path is added, and never follows its label, so renaming a path cannot cut
   * the link drawn from it.
   */
  const Decision: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const paths = decisionPaths(d);
    const leftOptions = ruleLeftOptions(variables, fieldKeys, t);
    const write = (next: DecisionPath[]) => onChange(patch(d, 'paths', next.map((p) => ({ id: p.id, label: p.label, match: p.match, rules: p.rules }))));
    const setPath = (i: number, next: Partial<DecisionPath>) => write(paths.map((p, j) => (j === i ? { ...p, ...next } : p)));
    const setRule = (i: number, k: number, next: Partial<DecisionPath['rules'][number]>) =>
      setPath(i, { rules: paths[i].rules.map((r, j) => (j === k ? { ...r, ...next } : r)) });
    const move = (i: number, by: number) => {
      const next = [...paths];
      const [moved] = next.splice(i, 1);
      next.splice(i + by, 0, moved);
      write(next);
    };
    const addPath = () => {
      const ids = new Set(paths.map((p) => p.id));
      let n = paths.length + 1;
      while (ids.has(`path_${n}`)) n++;
      write([...paths, { id: `path_${n}`, label: '', match: 'all', rules: [{ left: '', op: 'filled', right: '' }] }]);
    };
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Stack gap={8}>
          <Text role="inputLabel">{t('flows.builder.field.paths')}</Text>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.decisionOrderHint')}</Text>
          {paths.map((path, i) => (
            <div key={path.id} className="rr-inspector__decision-path">
              <div className="rr-inspector__decision-head">
                <TextInput
                  label={t('flows.builder.field.pathLabel', { n: i + 1 })}
                  placeholder={t('flows.builder.field.pathLabelPlaceholder')}
                  value={path.label}
                  maxLength={40}
                  onChange={(e) => setPath(i, { label: e.currentTarget.value })}
                />
                <Group gap={2} wrap="nowrap">
                  <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.field.moveUp')} disabled={i === 0} onClick={() => move(i, -1)}><ArrowIcon up /></ActionIcon>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.field.moveDown')} disabled={i === paths.length - 1} onClick={() => move(i, 1)}><ArrowIcon up={false} /></ActionIcon>
                  <ActionIcon variant="subtle" size="sm" aria-label={t('flows.builder.field.removePath')} onClick={() => write(paths.filter((_, j) => j !== i))}><CloseIcon /></ActionIcon>
                </Group>
              </div>
              {path.rules.length > 1 && (
                <Select
                  aria-label={t('flows.builder.field.pathMatch')}
                  value={path.match}
                  onChange={(e) => setPath(i, { match: e.currentTarget.value === 'any' ? 'any' : 'all' })}
                  data={[{ value: 'all', label: t('flows.builder.field.matchAll') }, { value: 'any', label: t('flows.builder.field.matchAny') }]}
                />
              )}
              {path.rules.map((rule, k) => {
                const right = opRight(rule.op);
                return (
                  <div key={k} className="rr-inspector__rule rr-inspector__rule--stacked">
                    <PickOrType
                      ariaLabel={t('flows.builder.field.ruleLeft')}
                      placeholder={t('flows.builder.field.ruleLeft')}
                      value={rule.left}
                      options={leftOptions}
                      onChange={(v) => setRule(i, k, { left: v })}
                      t={t}
                    />
                    <Select
                      aria-label={t('flows.builder.field.ruleOp')}
                      value={DECISION_OP_LIST.includes(rule.op) ? rule.op : 'eq'}
                      onChange={(e) => setRule(i, k, { op: e.currentTarget.value, right: opRight(e.currentTarget.value) === right ? rule.right : '' })}
                      data={DECISION_OP_LIST.map((op) => ({ value: op, label: t(`flows.builder.op.${op}`) }))}
                    />
                    {right === 'none' ? <span /> : (
                      <TextInput
                        aria-label={t('flows.builder.field.ruleRight')}
                        placeholder={right === 'date' ? t('flows.builder.field.dateRight') : right === 'days' ? t('flows.builder.field.daysRight') : t('flows.builder.field.ruleRight')}
                        inputMode={right === 'days' ? 'numeric' : right === 'number' ? 'decimal' : undefined}
                        list={right === 'date' ? 'rr-flow-date-words' : undefined}
                        value={rule.right}
                        onChange={(e) => setRule(i, k, { right: e.currentTarget.value })}
                      />
                    )}
                    <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => setPath(i, { rules: path.rules.filter((_, j) => j !== k) })}>
                      <CloseIcon />
                    </ActionIcon>
                  </div>
                );
              })}
              <Button size="xs" variant="subtle" onClick={() => setPath(i, { rules: [...path.rules, { left: '', op: 'filled', right: '' }] })}>+ {t('flows.builder.field.addRule')}</Button>
            </div>
          ))}
          <Button size="xs" variant="default" onClick={addPath}>+ {t('flows.builder.field.addPath')}</Button>
          <Text role="caption" c={MUTED}>{t('flows.builder.field.decisionExistsHint')}</Text>
        </Stack>
        <TextInput
          label={t('flows.builder.field.timezone')}
          description={t('flows.builder.field.decisionTimezoneHint')}
          value={asStr(d.timezone)}
          placeholder={'Africa/Johannesburg' /* i18n-ignore: a time zone name */}
          list="rr-flow-timezones"
          onChange={(e) => onChange(patch(d, 'timezone', e.currentTarget.value || undefined))}
        />
        <datalist id="rr-flow-timezones">
          {['Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Cairo', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'Asia/Dubai', 'UTC'].map((z) => <option key={z} value={z} />)}
        </datalist>
        <datalist id="rr-flow-date-words">
          {['today', 'tomorrow', 'yesterday'].map((w) => <option key={w} value={w} />) /* i18n-ignore: words the engine reads as dates */}
        </datalist>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Delay: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <div className="rr-inspector__row rr-inspector__row--delay">
          <NumberInput label={t('flows.builder.field.days')} value={typeof d.days === 'number' ? d.days : ''} min={0} max={7} onChange={(v) => onChange(patch(d, 'days', v === '' || v === 0 ? undefined : v))} />
          <NumberInput label={t('flows.builder.field.hours')} value={typeof d.hours === 'number' ? d.hours : ''} min={0} max={23} onChange={(v) => onChange(patch(d, 'hours', v === '' || v === 0 ? undefined : v))} />
          <NumberInput label={t('flows.builder.field.minutes')} value={typeof d.minutes === 'number' ? d.minutes : ''} min={0} max={59} onChange={(v) => onChange(patch(d, 'minutes', v === '' || v === 0 ? undefined : v))} />
          <NumberInput label={t('flows.builder.field.seconds')} value={typeof d.seconds === 'number' ? d.seconds : ''} min={0} max={59} onChange={(v) => onChange(patch(d, 'seconds', v === '' || v === 0 ? undefined : v))} />
        </div>
        <Text role="caption" c={MUTED}>{t('flows.builder.field.delayHint')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const End: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.endMessage')}
          description={varHint(variables, t)}
          value={asStr(d.message)}
          minRows={3}
          onChange={(e) => onChange(patch(d, 'message', e.currentTarget.value || undefined))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  const Generic: Inspector = ({ node, onChange }) => (
    <Stack gap="md">
      <NameFields d={dataOf(node)} onChange={onChange} t={t} />
      <Text role="bodySmall" c={MUTED}>{t('flows.builder.genericHint')}</Text>
      <JsonField value={dataOf(node)} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
    </Stack>
  );

  const Raw: Inspector = ({ node, onChange }) => (
    <Stack gap="md">
      <Text role="bodySmall" c={MUTED}>{t('flows.builder.rawHint')}</Text>
      <JsonField value={dataOf(node).__raw} requireObject={false} onApply={(p) => onChange({ __raw: p })} label={t('flows.builder.rawValue')} t={t} />
    </Stack>
  );

  /** Run another flow from here: the run carries on inside it with everything collected. */
  const RunFlow: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows(), staleTime: 30_000 });
    const all = list.data?.data ?? [];
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Select
          label={t('flows.builder.field.flowToRun')}
          description={t('flows.builder.field.flowToRunHint')}
          value={asStr(d.flow_id)}
          onChange={(e) => {
            const chosen = all.find((f) => f.id === e.currentTarget.value);
            onChange(patch(patch(d, 'flow_id', e.currentTarget.value || undefined), 'flow_name', chosen?.name));
          }}
          data={[{ value: '', label: t('flows.builder.field.flowChoose') }, ...all.map((f) => ({ value: f.id, label: f.is_active ? f.name : `${f.name} (${t('status.inactive')})` }))]}
        />
        <Text role="caption" c={MUTED}>{t('flows.builder.field.flowToRunNote')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Who to tell, and how: email, SMS, a WhatsApp message to a staff number, or the business's own system. */
  const Notify: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const rows = notifyChannels(d);
    const write = (next: Array<{ type: NotifyChannelType; target: string }>) =>
      onChange(patch(d, 'channels', next.map((c) => (c.type === 'webhook' ? { type: c.type, url: c.target } : { type: c.type, to: c.target }))));
    const placeholder = (type: NotifyChannelType) => (
      type === 'email' ? 'ops@example.com' : type === 'webhook' ? 'https://example.com/hooks/requests' : '+27 82 123 4567' // i18n-ignore: example addresses
    );
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="caption" c={MUTED}>{t('flows.builder.field.notifyIntro')}</Text>
        <Stack gap={6}>
          <Text role="inputLabel">{t('flows.builder.field.notifyChannels')}</Text>
          {rows.map((row, i) => (
            <div key={i} className="rr-inspector__row rr-inspector__row--channel">
              <Select
                aria-label={t('flows.builder.field.notifyVia')}
                value={row.type}
                onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, type: e.currentTarget.value as NotifyChannelType } : r)))}
                data={NOTIFY_CHANNEL_TYPES.map((v) => ({ value: v, label: t(`flows.builder.notifyChannel.${v}`) }))}
              />
              {row.type === 'sms' || row.type === 'whatsapp' ? (
                // A staff number, typed as it is dialled; the flow stores the
                // canonical form once it reads, and what was typed until then
                // so nothing is lost mid-keystroke.
                <PhoneInput
                  key={`${node.id}-${i}-${row.type}`}
                  aria-label={t('flows.builder.field.notifyTarget')}
                  defaultValue={row.target}
                  onChange={(e164, meta) => write(rows.map((r, j) => (j === i ? { ...r, target: meta.valid ? e164 : meta.raw } : r)))}
                  labels={phoneLabels(t)}
                />
              ) : (
                <TextInput
                  aria-label={t('flows.builder.field.notifyTarget')}
                  placeholder={placeholder(row.type)}
                  value={row.target}
                  onChange={(e) => write(rows.map((r, j) => (j === i ? { ...r, target: e.currentTarget.value } : r)))}
                />
              )}
              <ActionIcon variant="subtle" size="sm" aria-label={t('action.remove')} onClick={() => write(rows.filter((_, j) => j !== i))}>
                <CloseIcon />
              </ActionIcon>
            </div>
          ))}
          <Button size="xs" variant="subtle" onClick={() => write([...rows, { type: 'email', target: '' }])}>+ {t('flows.builder.field.addChannel')}</Button>
          {rows.some((r) => r.type === 'sms') && <Text role="caption" c={MUTED}>{t('flows.builder.field.smsHint')}</Text>}
          {rows.some((r) => r.type === 'whatsapp') && <Text role="caption" c={MUTED}>{t('flows.builder.field.whatsappStaffHint')}</Text>}
          {rows.some((r) => r.type === 'webhook') && <Text role="caption" c={MUTED}>{t('flows.builder.field.webhookPayloadHint')}</Text>}
        </Stack>
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.notifySubject')}
          description={varHint(variables, t)}
          value={asStr(d.subject)}
          placeholder={t('flows.builder.field.notifySubjectPlaceholder')}
          onChange={(e) => onChange(patch(d, 'subject', e.currentTarget.value || undefined))}
        />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.notifyMessage')}
          description={t('flows.builder.field.notifyMessageHint')}
          value={asStr(d.message)}
          minRows={3}
          onChange={(e) => onChange(patch(d, 'message', e.currentTarget.value || undefined))}
        />
        <Switch
          label={t('flows.builder.field.includeDetails')}
          description={t('flows.builder.field.includeDetailsHint')}
          checked={d.include_details !== false}
          onChange={(checked) => onChange(patch(d, 'include_details', checked ? undefined : false))}
        />
        <Switch
          label={t('flows.builder.field.includeTranscript')}
          description={t('flows.builder.field.includeTranscriptHint')}
          checked={d.include_transcript !== false}
          onChange={(checked) => onChange(patch(d, 'include_transcript', checked ? undefined : false))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * A text to the customer by SMS (4 October 2026). Telling the team is the
   * Notify step; this one reaches the customer, on their own number unless
   * "To" names another. One way on; `{{sms.ok}}` says whether it went.
   */
  const Sms: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const message = asStr(d.message);
    const status = useQuery({ queryKey: ['sms-status'], queryFn: () => sms.getStatus(), staleTime: 5 * 60_000, retry: false });
    const parts = useSmsParts(message);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        {status.data?.data?.available === false && <Alert color="warning">{t('flows.builder.field.smsUnavailable')}</Alert>}
        <Text role="caption" c={MUTED}>{t('flows.builder.field.smsIntro')}</Text>
        <Stack gap={4}>
          <TokenTextarea tokens={tokens} fallbackHint={hint}
            label={t('flows.builder.field.smsMessage')}
            description={varHint(variables, t)}
            value={message}
            minRows={3}
            onChange={(e) => onChange(patch(d, 'message', e.currentTarget.value))}
          />
          {parts && (
            <Text role="caption" c={tooLong(parts) ? 'var(--rr-color-error, #dc2626)' : MUTED}>
              {tooLong(parts)
                ? t('flows.builder.field.smsPartsTooMany', { n: parts.segments, max: parts.max_segments })
                : parts.segments === 1 ? t('flows.builder.field.smsPartsOne') : t('flows.builder.field.smsPartsMany', { n: parts.segments })}
              {message.includes('{{') ? ` ${t('flows.builder.field.smsPartsTokens')}` : ''}
            </Text>
          )}
        </Stack>
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.smsTo')}
          description={t('flows.builder.field.smsToHint')}
          value={asStr(d.to)}
          placeholder="{{phone}}"
          onChange={(e) => onChange(patch(d, 'to', e.currentTarget.value || undefined))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * An email to the customer (4 October 2026), the email twin of the SMS step.
   * To the contact's own address unless "To" names another; skipped when there
   * is no address or the customer opted out of email. One way on;
   * `{{email.ok}}` says whether it went and `{{email.detail}}` why not.
   */
  const Email: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const email = useEmailStatus();
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        {!email.isPending && !email.available && <Alert color="warning">{t('flows.builder.field.emailUnavailable')}</Alert>}
        <Text role="caption" c={MUTED}>{t('flows.builder.field.emailIntro')}</Text>
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.emailSubject')}
          description={t('flows.builder.field.emailSubjectHint')}
          value={asStr(d.subject)}
          onChange={(e) => onChange(patch(d, 'subject', e.currentTarget.value))}
        />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.emailMessage')}
          description={`${t('flows.builder.field.emailMessageHint')} ${varHint(variables, t)}`.trim()}
          value={asStr(d.message)}
          minRows={5}
          onChange={(e) => onChange(patch(d, 'message', e.currentTarget.value))}
        />
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.field.emailTo')}
          description={t('flows.builder.field.emailToHint')}
          value={asStr(d.to)}
          placeholder="{{email}}"
          onChange={(e) => onChange(patch(d, 'to', e.currentTarget.value || undefined))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** The value the service picker uses for "Let the client choose". Never a service id. */
  const CHOOSE_SERVICE = '__choose__';

  /**
   * Book (7 October 2026): which service (or the client's choice), with whom, how many days ahead, which of the
   * service's questions to ask, and whether to read it back first. Services and people come from the business's
   * Bookings setup; until there is one, the inspector says so and links to it.
   */
  const Booking: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const setup = useBookingSetup();
    const choose = bookingChoosesService(d);
    const serviceId = asStr(d.service_id);
    const service = choose ? undefined : setup.services.find((s) => s.id === serviceId);
    const staff = bookingStaff(d);
    const people = setup.staff.filter((p) => !service || !service.staff_ids?.length || service.staff_ids.includes(p.id));
    const questions = service?.questions ?? [];
    const asked = Array.isArray(d.ask) ? d.ask.map(String) : null;
    const notReady = setup.failed || (!setup.pending && setup.services.length === 0);
    const setAsked = (keys: string[]) => {
      const all = questions.every((q) => keys.includes(q.key));
      onChange(patch(d, 'ask', all ? undefined : questions.map((q) => q.key).filter((k) => keys.includes(k))));
    };
    const pickService = (value: string) => {
      let next = patch(patch(d, 'ask', undefined), 'service_name', undefined);
      if (value === CHOOSE_SERVICE || !value) {
        next = patch(patch(next, 'service_id', undefined), 'choose_service', true);
      } else {
        const chosen = setup.services.find((s) => s.id === value);
        next = patch(patch(patch(next, 'choose_service', undefined), 'service_id', value), 'service_name', chosen?.name);
      }
      onChange(next);
    };
    const serviceOptions = [
      { value: CHOOSE_SERVICE, label: t('flows.builder.booking.letClientChoose') },
      ...setup.services.map((s) => ({ value: s.id, label: s.name })),
      ...(!choose && serviceId && !service ? [{ value: serviceId, label: asStr(d.service_name) || t('flows.builder.booking.unknownService') }] : [])
    ];
    const staffOptions = [
      { value: 'any', label: t('flows.builder.booking.staffAny') },
      { value: 'choose', label: t('flows.builder.booking.staffChoose') },
      ...people.map((p) => ({ value: p.id, label: p.name })),
      ...(staff !== 'any' && staff !== 'choose' && !people.some((p) => p.id === staff) ? [{ value: staff, label: asStr(d.staff_name) || t('flows.builder.booking.unknownStaff') }] : [])
    ];
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="caption" c={MUTED}>{t('flows.builder.booking.intro')}</Text>
        {notReady && (
          <Alert color="warning" title={t('flows.builder.booking.setupTitle')}>
            <Stack gap={6}>
              <Text role="bodySmall">{t('flows.builder.booking.setupBody')}</Text>
              <Link to="/bookings/services">{t('flows.builder.booking.setupLink')}</Link>
            </Stack>
          </Alert>
        )}
        {setup.pending && <Skeleton height={36} />}
        <Select
          label={t('flows.builder.booking.service')}
          description={t('flows.builder.booking.serviceHint')}
          value={choose ? CHOOSE_SERVICE : serviceId}
          onChange={(e) => pickService(e.currentTarget.value)}
          data={serviceOptions}
        />
        <Select
          label={t('flows.builder.booking.staff')}
          description={t('flows.builder.booking.staffHint')}
          value={staff}
          onChange={(e) => {
            const value = e.currentTarget.value;
            const person = people.find((p) => p.id === value);
            onChange(patch(patch(d, 'staff', value), 'staff_name', person?.name));
          }}
          data={staffOptions}
        />
        <NumberInput
          label={t('flows.builder.booking.daysAhead')}
          description={t('flows.builder.booking.daysAheadHint', { max: BOOKING_DAYS_MAX })}
          value={typeof d.days_ahead === 'number' ? d.days_ahead : BOOKING_DAYS_DEFAULT}
          min={1}
          max={BOOKING_DAYS_MAX}
          onChange={(v) => onChange(patch(d, 'days_ahead', v === '' ? undefined : v))}
        />
        {service && questions.length > 0 && (
          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.builder.booking.questions')}</Text>
            {questions.map((q) => (
              <Checkbox
                key={q.key}
                label={q.label || q.key}
                checked={asked === null || asked.includes(q.key)}
                onChange={(checked) => {
                  const current = asked ?? questions.map((x) => x.key);
                  setAsked(checked ? [...current, q.key] : current.filter((k) => k !== q.key));
                }}
              />
            ))}
            <Text role="caption" c={MUTED}>{t('flows.builder.booking.questionsHint')}</Text>
          </Stack>
        )}
        {service && questions.length === 0 && <Text role="caption" c={MUTED}>{t('flows.builder.booking.noQuestions')}</Text>}
        {choose && <Text role="caption" c={MUTED}>{t('flows.builder.booking.questionsAll')}</Text>}
        <Switch
          label={t('flows.builder.booking.confirm')}
          description={t('flows.builder.booking.confirmHint')}
          checked={d.confirm !== false}
          onChange={(checked) => onChange(patch(d, 'confirm', checked))}
        />
        <Switch
          label={t('flows.builder.field.skipButton')}
          description={t('flows.builder.field.skipButtonHint')}
          checked={d.skip_button !== false}
          onChange={(checked) => onChange(patch(d, 'skip_button', checked))}
        />
        <Text role="caption" c={MUTED}>{t('flows.builder.booking.exits')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Manage booking: nothing to set beyond how far ahead a new time may be; the step explains itself. */
  const ManageBooking: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="bodySmall">{t('flows.builder.manageBooking.intro')}</Text>
        <Text role="caption" c={MUTED}>{t('flows.builder.manageBooking.exits')}</Text>
        <NumberInput
          label={t('flows.builder.booking.daysAhead')}
          description={t('flows.builder.manageBooking.daysAheadHint', { max: BOOKING_DAYS_MAX })}
          value={typeof d.days_ahead === 'number' ? d.days_ahead : BOOKING_DAYS_DEFAULT}
          min={1}
          max={BOOKING_DAYS_MAX}
          onChange={(v) => onChange(patch(d, 'days_ahead', v === '' ? undefined : v))}
        />
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * Ticket (7 October 2026): which type, priority and team (each the type's or the business's default unless
   * chosen), the subject and description with the run's variables, a value for each of the chosen type's
   * fields, tags, and whether the tickets service tells the client. The step's own `description` is the
   * ticket's description, so this inspector has no note field.
   */
  const Ticket: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const setup = useTicketSetup();
    const typeId = asStr(d.type_id);
    const type = setup.types.find((x) => x.id === typeId);
    const typeFields = type?.fields ?? [];
    const values = ticketFields(d);
    const valueOf = (key: string) => values.find((v) => v.key === key)?.value ?? '';
    const typeKeys = new Set(typeFields.map((f) => f.key));
    // Values written for keys the chosen type does not have (no type yet, or one set in the JSON).
    const [extra, setExtra] = useState(() => values.filter((v) => !typeKeys.has(v.key)).map((v) => ({ k: v.key, v: v.value })));
    const notReady = setup.failed;
    const setFields = (typed: Record<string, string>, others: Array<{ k: string; v: string }>) => {
      const all: Record<string, string> = {};
      for (const r of others) if (r.k.trim() && r.v.trim()) all[r.k.trim()] = r.v;
      for (const [k, v] of Object.entries(typed)) if (v.trim()) all[k] = v;
      onChange(patch(d, 'fields', Object.keys(all).length ? all : undefined));
    };
    const typedNow = (): Record<string, string> => Object.fromEntries(typeFields.map((f) => [f.key, valueOf(f.key)]));
    const pickType = (value: string) => {
      const chosen = setup.types.find((x) => x.id === value);
      let next = patch(patch(d, 'type_id', value || undefined), 'type_name', chosen?.name);
      // A value only means something for a field of the chosen type: the others are let go.
      const keep = new Set((chosen?.fields ?? []).map((f) => f.key));
      const kept = values.filter((v) => keep.has(v.key) && v.value.trim());
      next = patch(next, 'fields', kept.length ? Object.fromEntries(kept.map((v) => [v.key, v.value])) : undefined);
      setExtra([]);
      onChange(next);
    };
    const pick = (key: 'priority' | 'team', value: string, name?: string) =>
      onChange(patch(patch(d, `${key}_id`, value || undefined), `${key}_name`, value ? name : undefined));
    const priorityId = asStr(d.priority_id);
    const teamId = asStr(d.team_id);
    const typeOptions = [
      { value: '', label: t('flows.builder.ticket.typeDefault') },
      ...setup.types.map((x) => ({ value: x.id, label: x.name })),
      ...(typeId && !type ? [{ value: typeId, label: asStr(d.type_name) || t('flows.builder.ticket.unknownType') }] : [])
    ];
    const priorityOptions = [
      { value: '', label: t('flows.builder.ticket.priorityDefault') },
      ...setup.priorities.map((x) => ({ value: x.id, label: x.name })),
      ...(priorityId && !setup.priorities.some((x) => x.id === priorityId) ? [{ value: priorityId, label: asStr(d.priority_name) || t('flows.builder.ticket.unknownPriority') }] : [])
    ];
    const teamOptions = [
      { value: '', label: t('flows.builder.ticket.teamDefault') },
      ...setup.teams.map((x) => ({ value: x.id, label: x.name })),
      ...(teamId && !setup.teams.some((x) => x.id === teamId) ? [{ value: teamId, label: asStr(d.team_name) || t('flows.builder.ticket.unknownTeam') }] : [])
    ];
    const tags = ticketTags(d);
    return (
      <Stack gap="md">
        <TextInput
          label={t('flows.builder.field.name')}
          description={t('flows.builder.field.nameHint')}
          value={asStr(d.name)}
          onChange={(e) => onChange(patch(d, 'name', e.currentTarget.value || undefined))}
        />
        <Text role="caption" c={MUTED}>{t('flows.builder.ticket.intro')}</Text>
        {notReady && (
          <Alert color="warning" title={t('flows.builder.ticket.setupTitle')}>
            <Stack gap={6}>
              <Text role="bodySmall">{t('flows.builder.ticket.setupBody')}</Text>
              <Link to="/tickets/settings">{t('flows.builder.ticket.setupLink')}</Link>
            </Stack>
          </Alert>
        )}
        {setup.pending && <Skeleton height={36} />}
        <Select
          label={t('flows.builder.ticket.type')}
          description={t('flows.builder.ticket.typeHint')}
          value={typeId}
          onChange={(e) => pickType(e.currentTarget.value)}
          data={typeOptions}
        />
        <Select
          label={t('flows.builder.ticket.priority')}
          description={t('flows.builder.ticket.priorityHint')}
          value={priorityId}
          onChange={(e) => { const v = e.currentTarget.value; pick('priority', v, setup.priorities.find((x) => x.id === v)?.name); }}
          data={priorityOptions}
        />
        <Select
          label={t('flows.builder.ticket.team')}
          description={t('flows.builder.ticket.teamHint')}
          value={teamId}
          onChange={(e) => { const v = e.currentTarget.value; pick('team', v, setup.teams.find((x) => x.id === v)?.name); }}
          data={teamOptions}
        />
        <TokenInput tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.ticket.subject')}
          description={t('flows.builder.ticket.subjectHint')}
          value={typeof d.subject === 'string' ? d.subject : ''}
          placeholder={TICKET_DEFAULT_SUBJECT}
          onChange={(e) => onChange(patch(d, 'subject', e.currentTarget.value || undefined))}
        />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.ticket.description')}
          description={`${t('flows.builder.ticket.descriptionHint')} ${varHint(variables, t)}`.trim()}
          value={asStr(d.description)}
          minRows={3}
          onChange={(e) => onChange(patch(d, 'description', e.currentTarget.value || undefined))}
        />
        {typeFields.length > 0 && (
          <Stack gap={6}>
            <Text role="inputLabel">{t('flows.builder.ticket.fields')}</Text>
            {typeFields.map((f) => (
              <TokenInput key={f.key} tokens={tokens} fallbackHint={hint}
                label={f.label || f.key}
                description={[
                  f.required ? t('flows.builder.ticket.fieldRequired') : t('flows.builder.ticket.fieldOptional'),
                  f.options?.length ? t('flows.builder.ticket.fieldOptions', { options: f.options.join(', ') }) : ''
                ].filter(Boolean).join(' ')}
                value={valueOf(f.key)}
                onChange={(e) => { const v = e.currentTarget.value; setFields({ ...typedNow(), [f.key]: v }, extra); }}
              />
            ))}
          </Stack>
        )}
        {type && typeFields.length === 0 && <Text role="caption" c={MUTED}>{t('flows.builder.ticket.noFields')}</Text>}
        {(!type || extra.length > 0) && (
          <Stack gap={4}>
            <Text role="inputLabel">{t('flows.builder.ticket.otherFields')}</Text>
            <PairRows
              rows={extra}
              onChange={(rows) => { setExtra(rows); setFields(typedNow(), rows); }}
              keyLabel={t('flows.builder.ticket.fieldKey')}
              valueLabel={t('flows.builder.field.fieldValue')}
              addLabel={t('flows.builder.field.addField')}
              keyPlaceholder="address"
              valuePlaceholder="{{address}}"
              valueTokens={tokens}
              fallbackHint={hint}
              t={t}
            />
            <Text role="caption" c={MUTED}>{t('flows.builder.ticket.otherFieldsHint')}</Text>
          </Stack>
        )}
        <TextInput
          label={t('flows.builder.ticket.tags')}
          description={t('flows.builder.ticket.tagsHint')}
          value={tags.join(', ')}
          onChange={(e) => {
            const list = e.currentTarget.value.split(',').map((x) => x.trim()).filter(Boolean);
            onChange(patch(d, 'tags', list.length ? list : undefined));
          }}
        />
        <Switch
          label={t('flows.builder.ticket.notify')}
          description={t('flows.builder.ticket.notifyHint')}
          checked={d.notify_client !== false}
          onChange={(checked) => onChange(patch(d, 'notify_client', checked))}
        />
        <Text role="caption" c={MUTED}>{t('flows.builder.ticket.exits')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * Look up (19 October 2026): one of the business's live sources, the details it needs from the flow, and the name its
   * data points are kept under.
   */
  const Lookup: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    const apis = useApiSetup();
    const sourceId = asStr(d.source_id);
    const source = apis.sources.find((s) => s.id === sourceId) ?? null;
    const values: JsonObject = isPlainObject(d.values) ? d.values : {};
    const saveAs = asStr(d.save_as);
    const pick = (id: string) => {
      const s = apis.sources.find((x) => x.id === id);
      let next = patch(patch(d, 'source_id', id || undefined), 'source_name', s?.name);
      next = patch(next, 'save_as', s ? s.key || keyOf(s.name) : undefined);
      next = patch(next, 'points', s ? (s.response_paths ?? []).map((p) => p.key || keyOf(p.path)) : undefined);
      // A value only means something for a detail the chosen source takes.
      const keep = Object.fromEntries(Object.entries(values).filter(([k]) => (s?.parameters ?? []).some((p) => p.name === k)));
      next = patch(next, 'values', Object.keys(keep).length ? keep : undefined);
      onChange(next);
    };
    const setValue = (name: string, value: string) => {
      const next: JsonObject = { ...values, [name]: value };
      if (!value) delete next[name];
      onChange(patch(d, 'values', Object.keys(next).length ? next : undefined));
    };
    const points = Array.isArray(d.points) ? d.points.map(String) : [];
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Select
          label={t('flows.builder.lookup.source')}
          description={apis.sources.length === 0 && !apis.loading ? t('flows.builder.lookup.noSources') : t('flows.builder.lookup.sourceHint')}
          value={sourceId}
          onChange={(e) => pick(e.currentTarget.value)}
          data={[
            { value: '', label: t('flows.builder.lookup.sourcePick') },
            ...apis.sources.map((s) => ({ value: s.id, label: s.name })),
            ...(sourceId && !source && !apis.loading ? [{ value: sourceId, label: asStr(d.source_name) || t('flows.builder.lookup.sourceGone') }] : [])
          ]}
        />
        {source && (source.parameters ?? []).map((p) => (
          <TokenInput key={p.name} tokens={tokens} fallbackHint={hint}
            label={p.name}
            description={`${p.description}${p.required !== false ? '' : ` ${t('flows.builder.lookup.optional')}`}`}
            value={asStr(values[p.name])}
            placeholder={`{{${p.name}}}`}
            onChange={(e) => setValue(p.name, e.currentTarget.value)}
          />
        ))}
        {source && (
          <TextInput
            label={t('flows.builder.lookup.saveAs')}
            description={t('flows.builder.lookup.saveAsHint', { example: `{{${saveAs || 'key'}.${points[0] ?? 'field'}}}` })}
            value={saveAs}
            maxLength={40}
            onChange={(e) => onChange(patch(d, 'save_as', e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') || undefined))}
          />
        )}
        {source && saveAs && (
          <Stack gap={2}>
            <Text role="inputLabel">{t('flows.builder.lookup.variables')}</Text>
            <Text role="caption" c={MUTED} style={{ overflowWrap: 'anywhere' }}>
              {[...points, 'text', 'count', 'found'].map((p) => `{{${saveAs}.${p}}}`).join(' · ')}
            </Text>
          </Stack>
        )}
        {source && <CacheFields d={d} onChange={onChange} t={t} />}
        <Text role="caption" c={MUTED}>{t('flows.builder.lookup.exits')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /**
   * Change language (3 November 2026): what the step offers, an optional question of the author's own (the engine
   * asks its built-in one in the customer's language when there is none) and whether to say that the language changed.
   */
  const ChangeLanguage: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="bodySmall">{t('flows.builder.language.intro')}</Text>
        <ChatbotLanguages botId={attributes?.botId} t={t} />
        <TokenTextarea tokens={tokens} fallbackHint={hint}
          label={t('flows.builder.language.question')}
          description={t('flows.builder.language.questionHint')}
          value={asStr(d.question)}
          minRows={2}
          onChange={(e) => onChange(patch(d, 'question', e.currentTarget.value || undefined))}
          data-testid="language-step-question"
        />
        <Switch
          label={t('flows.builder.language.confirm')}
          description={t('flows.builder.language.confirmHint')}
          checked={d.confirm !== false}
          onChange={(checked) => onChange(patch(d, 'confirm', checked))}
          data-testid="language-step-confirm"
        />
        <Text role="caption" c={MUTED}>{t('flows.builder.language.exits')}</Text>
        <Text role="caption" c={MUTED}>{t('flows.builder.language.test')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  /** Ticket status: nothing to set; the step explains itself. */
  const TicketStatus: Inspector = ({ node, onChange }) => {
    const d = dataOf(node);
    return (
      <Stack gap="md">
        <NameFields d={d} onChange={onChange} t={t} />
        <Text role="bodySmall">{t('flows.builder.ticketStatus.intro')}</Text>
        <Text role="caption" c={MUTED}>{t('flows.builder.ticketStatus.exits')}</Text>
        <JsonField value={d} onApply={(p) => onChange(p as JsonObject)} label={t('flows.builder.allFields')} t={t} />
      </Stack>
    );
  };

  return {
    [TRIGGER_NODE_TYPE]: Trigger,
    send: Send,
    reply: Send,
    template: Template,
    ask: Ask,
    form: Form,
    booking: Booking,
    manage_booking: ManageBooking,
    ticket: Ticket,
    ticket_status: TicketStatus,
    language: ChangeLanguage,
    lookup: Lookup,
    llm: Llm,
    http: Http,
    contact: Contact,
    handoff: Handoff,
    notify: Notify,
    sms: Sms,
    email: Email,
    flow: RunFlow,
    set: SetVar,
    condition: Condition,
    decision: Decision,
    switch: SwitchNode,
    hours: Hours,
    split: Split,
    delay: Delay,
    end: End,
    [RAW_NODE_TYPE]: Raw,
    __generic: Generic
  };
}

/** A text longer than the platform will send. */
const tooLong = (p: sms.SmsPreview): boolean => p.max_segments > 0 && p.segments > p.max_segments;

/**
 * How many SMS parts a text takes, as the platform counts them, asked for a
 * moment after the typing stops. Null while there is nothing to count or the
 * count could not be had: the counter is a guide, so a failure stays quiet.
 */
function useSmsParts(text: string): sms.SmsPreview | null {
  const [parts, setParts] = useState<sms.SmsPreview | null>(null);
  useEffect(() => {
    if (!text.trim()) { setParts(null); return; }
    let live = true;
    const timer = window.setTimeout(() => {
      sms.preview({ text, marketing: false })
        .then((res) => { if (live) setParts(res.data ?? null); })
        .catch(() => { if (live) setParts(null); });
    }, 400);
    return () => { live = false; window.clearTimeout(timer); };
  }, [text]);
  return parts;
}

/** How many {{n}} placeholders a template's body carries. `components` is the JSON string channel-wa stores. */
function countPlaceholders(components: string | undefined): number {
  if (!components) return 0;
  try {
    const parsed: unknown = JSON.parse(components);
    if (!Array.isArray(parsed)) return 0;
    const body = parsed.find((c) => isPlainObject(c) && String(c.type ?? '').toUpperCase() === 'BODY');
    const text = body && isPlainObject(body) && typeof body.text === 'string' ? body.text : '';
    const nums = [...text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
    return nums.length ? Math.max(...nums) : 0;
  } catch { return 0; }
}

function hasMediaHeader(components: string | undefined): boolean {
  if (!components) return false;
  try {
    const parsed: unknown = JSON.parse(components);
    if (!Array.isArray(parsed)) return false;
    const header = parsed.find((c) => isPlainObject(c) && String(c.type ?? '').toUpperCase() === 'HEADER');
    const format = header && isPlainObject(header) ? String(header.format ?? 'TEXT').toUpperCase() : 'TEXT';
    return format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT';
  } catch { return false; }
}
